// @ts-nocheck
// =============================================================================
//  KitchenControl — MEP Module
//  Props: { config, synced, onConfigSave, activeTab, onTabChange }
//  activeTab: 'lijst' | 'afdeling' | 'categorie' | 'nieuwitem' | 'archief'
// =============================================================================
import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import {
  ClipboardList, Building2, FolderPlus, PlusCircle,
  Archive, X, Plus, Trash2, ChevronDown, Pencil, Check,
  ChevronRight, Search, Package, Printer, Thermometer,
} from "lucide-react";
import { useWhiteboard, WhiteboardButton, WhiteboardSheet } from "./Whiteboard";
import { Hint } from "./Hint";
import { ModuleWelkom, WelkomKnop } from "./ModuleWelkom";
import { useT } from "./i18n";
import { activeCoolingItemIds } from "./coolingLogic.mjs";
import "./coolingPulse.css";
const CoolingContext = createContext(new Set());
import { useBeheerPin } from "./ui/BeheerPin";

// ── Kleuren ───────────────────────────────────────────────────────────────────
const C = {
  bg:      "#F8FAFC",
  bg2:     "#FFFFFF",
  bg3:     "#F1F5F9",
  hdr:     "#0F172A",
  hdrBdr:  "#1E293B",
  txt:     "#0F172A",
  txt2:    "#475569",
  txt3:    "#94A3B8",
  bdr:     "#E2E8F0",
  bdr2:    "#CBD5E1",
  pri:     "#2563EB",
  priBg:   "#EFF6FF",
  priBd:   "#BFDBFE",
  green:   "#16A34A",
  greenBg: "#DCFCE7",
  greenBd: "#BBF7D0",
  amber:   "#D97706",
  amberBg: "#FFFBEB",
  amberBd: "#FDE68A",
  red:     "#DC2626",
  redBg:   "#FEE2E2",
  redBd:   "#FCA5A5",
};

// ── Constanten ────────────────────────────────────────────────────────────────
const DAYS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
const UNITS = ["kg", "g", "L", "ml", "stuks", "bos", "blik", "zak", "el", "tl", "snuf", "overig"];
const todayIdx = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
const uid = () => `${Date.now()}-${Math.floor(Math.random() * 9999)}`;

// ── ISO week helpers ──────────────────────────────────────────────────────────
function isoWeekOf(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return { year: d.getUTCFullYear(), week: Math.ceil(((d - yearStart) / 86400000 + 1) / 7) };
}
function targetDate(dayIdx, weekOffset = 0) {
  const now = new Date();
  const cur = now.getDay() === 0 ? 6 : now.getDay() - 1;
  const t = new Date(now);
  t.setDate(t.getDate() + (dayIdx - cur + weekOffset * 7));
  return t;
}
function dayKey(dayName, weekOffset = 0) {
  const idx = DAYS.indexOf(dayName);
  if (idx < 0) return dayName;
  const { year, week } = isoWeekOf(targetDate(idx, weekOffset));
  return `${year}-W${String(week).padStart(2, "0")}-${dayName}`;
}
function getWeekNr(weekOffset = 0) {
  const now = new Date();
  const d = new Date(now);
  d.setDate(d.getDate() + weekOffset * 7);
  return isoWeekOf(d).week;
}
function getWeekLabel(weekOffset = 0) {
  const cur = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
  const mon = new Date(); mon.setDate(mon.getDate() - cur + weekOffset * 7);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const M = ["jan","feb","mrt","apr","mei","jun","jul","aug","sep","okt","nov","dec"];
  return `${mon.getDate()} ${M[mon.getMonth()]} – ${sun.getDate()} ${M[sun.getMonth()]}`;
}
function getDayDate(dayIdx, weekOffset = 0) {
  return targetDate(dayIdx, weekOffset).getDate();
}

// ── Lokale opslag ─────────────────────────────────────────────────────────────
const LS_KEY = "kc-mep-v1";
function loadLocal() {
  try {
    const s = localStorage.getItem(LS_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return null;
}
function saveLocal(state) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch {}
}

// ── Laatst gekozen afdeling onthouden ─────────────────────────────────────────
// Bewust een APARTE sleutel (niet in de sync-blob): de afdelingskeuze is een
// weergave-voorkeur per apparaat en mag niet meeliften op de gedeelde mep_data,
// zodat een binnenkomende sync 'm nooit overschrijft. Overleeft het verlaten en
// heropenen van de MEP-module (bijv. na het openen van een gekoppeld recept).
// Valt onder de "kc-"-prefix, dus wordt bij een tenantwissel netjes gewist.
const AFD_KEY = "kc-mep-afd-v1";
function loadActiveAfd() {
  try { return localStorage.getItem(AFD_KEY) || null; } catch { return null; }
}
function saveActiveAfd(id) {
  try { if (id) localStorage.setItem(AFD_KEY, id); } catch {}
}


// ── Supabase sync-laag ────────────────────────────────────────────────────────
// Props sb + tenantId worden meegegeven vanuit App.jsx (zelfde patroon als HACCP)
// Tabel: mep_data { id, tenant_id, data jsonb, updated_at }
// Realtime: luistert op UPDATE van eigen tenant-rij → merge binnenkomende data

async function sbLoad(sb, tenantId) {
  try {
    const rows = await sb.select("mep_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
    if (rows?.length && rows[0]?.data) return rows[0].data;
  } catch (e) { console.warn("[MEP] load failed:", e); }
  return null;
}

async function sbSave(sb, tenantId, data) {
  const updated_at = new Date().toISOString();
  try {
    // Haal de huidige serverrij op (bepaalt insert vs update én dient als veiligheidsslot).
    const rows = await sb.select("mep_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
    // VEILIGHEIDSSLOT: een lege/standaardlijst (0 items, hooguit 1 afdeling) mag
    // NOOIT een gevulde serverlijst overschrijven. Voorkomt dataverlies wanneer de
    // app per ongeluk met een seed/lege staat zou wegschrijven (hapering bij laden,
    // raar inlog-moment, enz.).
    const seedAchtig = !data?.items?.length && (data?.afdelingen?.length ?? 0) <= 1;
    if (seedAchtig && rows?.length && rows[0]?.data?.items?.length) {
      console.warn("[MEP] save geblokkeerd: lege/standaardlijst zou een gevulde lijst overschrijven.");
      return null;
    }
    if (rows?.length) {
      await sb.update("mep_data", `tenant_id=eq.${tenantId}`, { data, updated_at });
    } else {
      await sb.insert("mep_data", { tenant_id: tenantId, data, updated_at });
    }
    return updated_at; // teruggeven zodat we onze eigen echo herkennen
  } catch (e) { console.warn("[MEP] save failed:", e); return null; }
}

// Merge inkomende realtime state met lokale state
// Kritiek: checks worden per item+dag gemerged — niet overschreven
// Zo gaan aanvinkjes van device 2 niet verloren als device 1 iets anders aanpast
function mergeState(local, remote) {
  if (!remote) return local;
  // Een binnenkomende (nieuwere) sync is leidend — óók voor vinkjes.
  // De oude "union"-merge voegde lokale + remote vinkjes samen en kon daardoor
  // een wéggehaald vinkje terugzetten (waardoor bestelartikelen bleven staan).
  // De echo-guard in applyRemote zorgt dat we onze eigen, nog niet opgeslagen
  // wijziging niet per ongeluk met een oudere sync overschrijven.
  return remote;
}

// ── Initiële staat ────────────────────────────────────────────────────────────
function initState() {
  const saved = loadLocal();
  if (saved?.afdelingen?.length) return saved;
  const afdId = uid();
  return {
    afdelingen: [{ id: afdId, naam: "Warme kant" }],
    categorieen: [
      { id: uid(), naam: "Vlees",            afdeling_id: afdId },
      { id: uid(), naam: "Vis & Zeevruchten", afdeling_id: afdId },
      { id: uid(), naam: "Soepen",            afdeling_id: afdId },
      { id: uid(), naam: "Overig",            afdeling_id: afdId },
    ],
    items: [],
  };
}

// ── Gedeelde stijlen ──────────────────────────────────────────────────────────
const inp = {
  background: C.bg2, border: `1px solid ${C.bdr}`, color: C.txt,
  padding: "7px 10px", fontFamily: "inherit", fontSize: 13,
  borderRadius: 6, outline: "none", width: "100%", boxSizing: "border-box",
};
const btn = (bg, fg, bd) => ({
  background: bg, color: fg, border: `1px solid ${bd || bg}`,
  padding: "7px 14px", fontFamily: "inherit", fontSize: 12,
  fontWeight: 600, borderRadius: 6, cursor: "pointer",
});

// ─────────────────────────────────────────────────────────────────────────────
// MEP MODULE
// ─────────────────────────────────────────────────────────────────────────────
export default function MepModule({ config, onConfigSave, activeTab, onTabChange, sb, tenantId, onOpenRecept, supabaseClient, userRole, onStartCooling }) {
  const { requireBeheer } = useBeheerPin();
  const { t } = useT();
  const [state, setState] = useState(initState);
  const [coolingRows, setCoolingRows] = useState([]);
  const [coolingError, setCoolingError] = useState(false);
  useEffect(() => {
    let alive = true, pending = false;
    setCoolingRows([]); setCoolingError(false);
    const refresh = async () => {
      if (pending || !sb || !tenantId) return;
      pending = true;
      try {
        const records = [];
        for (let offset = 0; ; offset += 500) {
          const rows = await sb.selectStrict("haccp_terugkoel", "tenant_id=eq." + tenantId + "&select=id,product,started_at,eind_tijd,eind_temp,source_mep_item_id&or=(eind_tijd.is.null,eind_tijd.eq.,eind_temp.is.null)&order=created_at.asc,id.asc&limit=500&offset=" + offset);
          if (!Array.isArray(rows)) throw new Error("Cooling status unavailable");
          records.push(...rows);
          if (rows.length < 500) break;
        }
        if (alive) { setCoolingRows(records); setCoolingError(false); }
      } catch { if (alive) setCoolingError(true); }
      finally { pending = false; }
    };
    refresh();
    const timer = window.setInterval(refresh, 15000);
    window.addEventListener("focus", refresh);
    return () => { alive = false; clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [sb, tenantId]);
  const coolingIds = activeCoolingItemIds(coolingRows, state.items || []);
  const [weekOffset, setWeekOffset] = useState(0);
  // activeAfdId los initialiseren: eerst de laatst onthouden afdeling, anders de
  // eerste afdeling uit de opgeslagen data. Zo blijf je na het openen/sluiten van
  // een gekoppeld recept op dezelfde afdeling staan i.p.v. terug te vallen op de eerste.
  const [activeAfdId, setActiveAfdId] = useState(() => {
    const onthouden = loadActiveAfd();
    if (onthouden) return onthouden;
    const s = loadLocal();
    if (s?.afdelingen?.length) return s.afdelingen[0].id;
    // Geen opgeslagen data: pak de id uit de verse state
    return null; // wordt gereset door useEffect hieronder
  });
  const [openCats, setOpenCats] = useState({});     // { catId: true } = uitgeklapt
  const [activeDag, setActiveDag] = useState(() => new Date().getDay() === 0 ? 6 : new Date().getDay() - 1); // 0=Ma..6=Zo
  const [lijstModus, setLijstModus] = useState("volledig"); // "volledig" | "werk"
  const magBeheren = userRole !== "medewerker"; // medewerker mag niet bewerken, wél etiketten printen
  const [itemPopup, setItemPopup] = useState(null); // null | { mode:'new'|'edit', item? }
  const [printItem, setPrintItem] = useState(null); // direct etiket printen vanuit de dagelijkse lijst
  const [printVraagItem, setPrintVraagItem] = useState(null); // KC_MEP_DAGELIJKSE_PRINTFLOW: vraag na afronden
  const [losEtiketOpen, setLosEtiketOpen] = useState(false); // los etiket printen (naam bestaat niet als mep-item)
  const [structuurTab, setStructuurTab] = useState(null);
  const [beheerOpen, setBeheerOpen] = useState(false);
  const beheerRef = useRef(null);
  useEffect(() => {
    if (!beheerOpen) return;
    const buiten = e => { if (!beheerRef.current?.contains(e.target)) setBeheerOpen(false); };
    const escape = e => { if (e.key === "Escape") setBeheerOpen(false); };
    document.addEventListener("pointerdown", buiten);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", buiten); document.removeEventListener("keydown", escape); };
  }, [beheerOpen]);
  const [search, setSearch] = useState("");
  const [synced, setSynced] = useState(false);
  const [syncStatus, setSyncStatus] = useState("local");
  const lastUpdatedAtRef = useRef(null);
  const pollRef = useRef(null);
  const applyingRemoteRef = useRef(false); // voorkomt save-echo na remote apply
  const [leveranciers, setLeveranciers] = useState([]); // namen uit HACCP-config, voor bestelartikelen
  const [medewerkers, setMedewerkers] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kc-team-v1") || "{}")?.medewerkers || []; } catch { return []; }
  }); // voor de etiketprint — wordt zo uit team_data ververst
  const [artikelen, setArtikelen] = useState([]); // gedeelde suggestielijst (recepten/whiteboard/mep)
  const artikelenRef = useRef([]);
  useEffect(() => { artikelenRef.current = artikelen; }, [artikelen]);

  // ── Whiteboard (gedeeld op/bijna-op-bord) ──
  const wb = useWhiteboard(sb, tenantId, supabaseClient);
  const [wbOpen, setWbOpen] = useState(false);

  // Zijbalk-navigatie triggers
  useEffect(() => {
    if (activeTab === "nieuwitem")  { (async () => { if (await requireBeheer("een MEP-item toe te voegen")) setItemPopup({ mode: "new" }); onTabChange?.("lijst"); })(); }
    if (activeTab === "afdeling")   { (async () => { if (await requireBeheer("afdelingen te beheren")) setStructuurTab("afdelingen"); onTabChange?.("lijst"); })(); }
    if (activeTab === "categorie")  { (async () => { if (await requireBeheer("categorieën te beheren")) setStructuurTab("categorieen"); onTabChange?.("lijst"); })(); }
  }, [activeTab]);

  // HACCP-leveranciers ophalen voor de keuzelijst bij bestelartikelen
  useEffect(() => {
    if (!sb || !tenantId) return;
    (async () => {
      try {
        const rows = await sb.select("haccp_config", `tenant_id=eq.${tenantId}&select=instellingen&limit=1`);
        const lev = rows?.[0]?.instellingen?.leveranciers || [];
        setLeveranciers(lev.map(l => l.naam).filter(Boolean));
      } catch {}
    })();
  }, [sb, tenantId]);

  // Medewerkers ophalen uit team_data voor de etiketprint. Stond eerder alleen in
  // localStorage (kc-team-v1), die pas gevuld wordt zodra de Team-module geopend is —
  // in een vers venster bleef de keuzelijst daardoor leeg.
  useEffect(() => {
    if (!sb || !tenantId) return;
    (async () => {
      try {
        const rows = await sb.select("team_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
        setMedewerkers(rows?.[0]?.data?.medewerkers || []);
      } catch {}
    })();
  }, [sb, tenantId]);

  // Gedeelde artikellijst ophalen voor de autocomplete bij bestelartikelen
  useEffect(() => {
    if (!sb || !tenantId) return;
    let actief = true;
    (async () => {
      try {
        const rows = await sb.select("artikelen", `tenant_id=eq.${tenantId}&select=data&limit=1`);
        if (actief && Array.isArray(rows?.[0]?.data)) setArtikelen(rows[0].data);
      } catch {}
    })();
    return () => { actief = false; };
  }, [sb, tenantId]);

  // Nieuw ingevoerd bestelartikel onthouden in de gedeelde lijst (groeiende suggesties,
  // gedeeld met Recepten en het whiteboard)
  const onthoudArtikel = useCallback(async (naam) => {
    const n = (naam || "").trim();
    if (!n) return;
    const huidig = artikelenRef.current || [];
    if (huidig.some(a => a.toLowerCase() === n.toLowerCase())) return; // al bekend
    const lijst = [...huidig, n].sort((a, b) => a.localeCompare(b, "nl"));
    setArtikelen(lijst);
    artikelenRef.current = lijst;
    if (!sb || !tenantId) return;
    try {
      const exists = await sb.select("artikelen", `tenant_id=eq.${tenantId}&select=tenant_id&limit=1`);
      if (exists?.length) await sb.update("artikelen", `tenant_id=eq.${tenantId}`, { data: lijst, updated_at: new Date().toISOString() });
      else await sb.insert("artikelen", { tenant_id: tenantId, data: lijst, updated_at: new Date().toISOString() });
    } catch {}
  }, [sb, tenantId]);

  const { afdelingen, categorieen, items } = state;
  const tab = activeTab === "archief" ? "archief" : "lijst";

  // update: sync naar state + localStorage (save loopt via useEffect)
  const update = useCallback((fn) => setState(s => {
    const next = { ...s, ...fn(s) };
    saveLocal(next);
    try { window.dispatchEvent(new CustomEvent("kc:mep-data", { detail: next })); } catch {}
    return next;
  }), []);

  // Pas remote data toe ZONDER een nieuwe save te triggeren (echo-guard).
  // Merge altijd tegen de actuele state (functionele updater) zodat lokale
  // vinkjes niet verloren gaan, en onthoud de timestamp.
  const applyRemote = useCallback((remoteData, stamp) => {
    if (!remoteData?.afdelingen) return;
    applyingRemoteRef.current = true;
    lastUpdatedAtRef.current = stamp || null;
    setState(cur => {
      const merged = mergeState(cur, remoteData);
      saveLocal(merged);
      return merged;
    });
  }, []);

  // ── 1. Load van Supabase bij mount ───────────────────────────────────────────
  // Auto-save (effect 2) gaat PAS aan na een GESLAAGDE load. sb.select gooit geen
  // fout maar geeft [] bij een mislukte load — daarom: als de server NIETS teruggeeft
  // terwijl er lokaal wél een gevulde lijst is, behandelen we dat als een mislukte
  // load (hapering/raar inlog-moment): we syncen NIET en proberen opnieuw. Zo kan een
  // lege/standaardlijst nooit de echte lijst op de server overschrijven.
  useEffect(() => {
    if (!sb || !tenantId) return; // geen tenant: niets laden én auto-save niet aanzetten
    let cancelled = false;
    let pogingen = 0;
    const probeer = async () => {
      setSyncStatus("syncing");
      const rows = await sb.select("mep_data", `tenant_id=eq.${tenantId}&select=data,updated_at&limit=1`);
      if (cancelled) return;
      const serverData = rows?.length ? rows[0]?.data : null;
      if (serverData?.afdelingen?.length) {
        // Server gaf een bruikbare rij terug → toepassen + auto-save mag aan
        applyingRemoteRef.current = true; // initiële load niet meteen terugschrijven
        lastUpdatedAtRef.current = rows[0].updated_at;
        setState(cur => {
          const merged = mergeState(cur, serverData);
          saveLocal(merged);
          return merged;
        });
        setSyncStatus("synced");
        setSynced(true);
        return;
      }
      // Server gaf niets bruikbaars terug. Lokaal wél een gevulde lijst? → verdacht.
      const lokaal = loadLocal();
      if (lokaal?.items?.length) {
        console.warn("[MEP] load gaf niets terug terwijl er lokaal data is; auto-save uitgesteld, opnieuw proberen (poging " + (pogingen + 1) + ").");
        setSyncStatus("local");
        if (!cancelled && pogingen < 5) { pogingen++; setTimeout(probeer, 3000); }
        return;
      }
      // Geen serverdata én geen lokale data → tenant is echt (nog) leeg → sync mag aan.
      setSyncStatus("synced");
      setSynced(true);
    };
    probeer();
    return () => { cancelled = true; };
  }, [sb, tenantId]);

  // ── 2. Save bij state-wijziging — ALLEEN als synced ──────────────────────────
  useEffect(() => {
    if (!synced) return;
    // Kwam deze state-wijziging van een remote apply? Dan niet terugschrijven.
    if (applyingRemoteRef.current) { applyingRemoteRef.current = false; return; }
    if (!sb || !tenantId) return;
    setSyncStatus("syncing");
    const to = setTimeout(async () => {
      const stamp = await sbSave(sb, tenantId, state);
      if (stamp) lastUpdatedAtRef.current = stamp; // eigen schrijf onthouden
      setSyncStatus("synced");
    }, 1200);
    return () => clearTimeout(to);
  }, [state, synced]);

  // ── 3. Poll elke 5 seconden als fallback (als realtime niet beschikbaar is) ───
  useEffect(() => {
    if (!sb || !tenantId) return;
    const poll = async () => {
      if (!synced) return;
      try {
        const rows = await sb.select("mep_data", `tenant_id=eq.${tenantId}&select=data,updated_at&limit=1`);
        if (!rows?.length || !rows[0].updated_at) return;
        if (rows[0].updated_at === lastUpdatedAtRef.current) return; // niets nieuws / eigen echo
        if (rows[0].data?.afdelingen?.length) applyRemote(rows[0].data, rows[0].updated_at);
      } catch {}
    };
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [sb, tenantId, synced]);

  // ── 4. Supabase Realtime WebSocket voor instant sync ────────────────────────
  useEffect(() => {
    if (!supabaseClient || !tenantId || !synced) return;
    const channel = supabaseClient
      .channel(`mep_data_${tenantId}`)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "mep_data",
        filter: `tenant_id=eq.${tenantId}`,
      }, (payload) => {
        const remote = payload.new?.data;
        const stamp = payload.new?.updated_at || null;
        if (stamp && stamp === lastUpdatedAtRef.current) return; // eigen echo overslaan
        if (remote?.afdelingen) applyRemote(remote, stamp);     // merge ipv overschrijven
      })
      .subscribe();
    return () => supabaseClient.removeChannel(channel);
  }, [supabaseClient, tenantId, synced]);

  // ── Actieve afdeling instellen: null (eerste load) of verdwenen afdeling → eerste
  useEffect(() => {
    if (afdelingen.length && (!activeAfdId || !afdelingen.find(a => a.id === activeAfdId))) {
      setActiveAfdId(afdelingen[0].id);
    }
  }, [afdelingen, activeAfdId]);

  // ── Laatst gekozen afdeling bewaren (overleeft het verlaten/heropenen van MEP) ─
  useEffect(() => {
    if (activeAfdId) saveActiveAfd(activeAfdId);
  }, [activeAfdId]);

  // Toggle check: leeg → gepland(amber) → gedaan(groen) → leeg.
  // Bij gepland → gedaan vragen we meteen of er een etiket geprint moet worden.
  const toggleCheck = (itemId, dk, opties = {}) => {
    const huidigItem = items.find(it => it.id === itemId);
    const cur = huidigItem?.checks?.[dk] || 0;
    const wordtKlaar = cur === 1;
    update(s => ({
      items: s.items.map(it => {
        if (it.id !== itemId) return it;
        const huidig = it.checks?.[dk] || 0;
        const next = (huidig + 1) % 3;
        const checks = { ...it.checks, [dk]: next };
        if (next === 0) delete checks[dk];
        return { ...it, checks };
      })
    }));
    if (wordtKlaar && !opties.geenPrintVraag && huidigItem) setPrintVraagItem(huidigItem);
  };

  // Geplande bestelartikelen voor het Whiteboard berekenen
  const geplandeBestelartikelen = (() => {
    const map = {};
    items.filter(it => !it.archived).forEach(it => {
      const heeftPlan = DAYS.some((d, i) => {
        const dk = dayKey(d, weekOffset);
        return (it.checks?.[dk] || 0) === 1; // alleen gepland (amber) — niet gedaan/leeg
      });
      if (!heeftPlan) return;
      (it.bestelartikelen || []).forEach(ba => {
        if (!ba.naam?.trim()) return;
        const k = `${ba.naam.trim().toLowerCase()}|${ba.eenheid}`;
        if (!map[k]) map[k] = { naam: ba.naam.trim(), eenheid: ba.eenheid, hoeveelheid: 0, leverancier: ba.leverancier || "Overig", bronnen: [] };
        map[k].hoeveelheid += parseFloat(ba.hoeveelheid) || 0;
        if (!map[k].bronnen.includes(it.naam)) map[k].bronnen.push(it.naam);
      });
    });
    const all = Object.values(map).sort((a, b) => a.naam.localeCompare(b.naam, "nl"));
    return all;
  })();

  const mepWbWeekInfo = isoWeekOf(targetDate(0, weekOffset));
  const mepWbWeekKey = `${mepWbWeekInfo.year}-W${String(mepWbWeekInfo.week).padStart(2, "0")}`;
  const mepGeregeld = new Set(state.whiteboard_geregeld?.[mepWbWeekKey] || []);
  const mepWhiteboardItems = geplandeBestelartikelen
    .filter((r) => !mepGeregeld.has(String(r.naam || "").trim().toLowerCase()))
    .map((r, index) => ({
      id: `mep-bestel-${index}-${String(r.naam || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
      tekst: r.naam || "",
      via: (r.bronnen || []).join(", "),
      bron: "mep",
      readonly: true,
      prioriteit: "mep",
      mepKey: String(r.naam || "").trim().toLowerCase(),
      weekKey: mepWbWeekKey,
    }));

  const regelMepBestelartikel = (it) => {
    const key = String(it?.mepKey || it?.tekst || "").trim().toLowerCase();
    if (!key) return;
    const wk = it?.weekKey || mepWbWeekKey;
    update((cur) => {
      const geregeld = { ...(cur.whiteboard_geregeld || {}) };
      const lijst = Array.isArray(geregeld[wk]) ? geregeld[wk] : [];
      geregeld[wk] = lijst.includes(key) ? lijst : [...lijst, key];
      return { whiteboard_geregeld: geregeld };
    });
  };

  return (
    <CoolingContext.Provider value={coolingIds}>
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: C.bg, fontFamily: "system-ui,-apple-system,sans-serif", fontSize: 13, color: C.txt, overflow: "hidden" }}>
      {/* ── Header ── */}
      <header style={{ background: C.hdr, borderBottom: `1px solid ${C.hdrBdr}`, height: 52, display: "flex", alignItems: "center", padding: "0 18px", gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "#fff", flexShrink: 0, whiteSpace: "nowrap" }}>
          {tab === "archief" ? "🗂 " + t("Archief") : "📋 " + t("Mise En Place")}
        </span>
        {/* Knoppen: horizontaal schuifbaar op smalle schermen, zodat niets buiten
            beeld valt. De titel hierboven blijft altijd links staan. Let op: geen
            justifyContent:flex-end — dat blokkeert naar-links-scrollen, waardoor
            de eerste knoppen (Whiteboard, sync) onbereikbaar zouden wegvallen. */}
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12, overflowX: "auto", overflowY: "hidden", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", minWidth: 0 }}>
          {/* Spacer: duwt de knoppen naar rechts als er ruimte is (breed scherm),
              maar krimpt weg op smalle schermen zodat het scrollen blijft werken. */}
          <div style={{ flex: "1 1 auto", minWidth: 0 }} />
          <WelkomKnop module="mep" />
        <WhiteboardButton count={wb.items.length + mepWhiteboardItems.length} onClick={() => setWbOpen(true)} />
        {/* Sync status */}
        {sb && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: syncStatus === "syncing" ? "#FCD34D" : syncStatus === "error" ? "#FCA5A5" : "rgba(255,255,255,.45)", flexShrink: 0, whiteSpace: "nowrap" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: syncStatus === "syncing" ? "#FCD34D" : syncStatus === "error" ? "#F87171" : "#4ADE80", flexShrink: 0 }} />
            {syncStatus === "syncing" ? t("synchroniseren…") : syncStatus === "error" ? t("sync fout") : t("gesynchroniseerd")}
          </div>
        )}
        {tab === "lijst" && lijstModus === "volledig" && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <button
              onClick={() => setLosEtiketOpen(true)}
              title={t("Los etiket printen")}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.2)", color: "#fff", padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontFamily: "inherit", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}
            >
              <Printer size={14} /> {t("Los etiket")}
            </button>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <Search size={13} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: C.txt3, pointerEvents: "none" }} />
              <input
                style={{ ...inp, width: 140, padding: "5px 8px 5px 26px", fontSize: 12, background: C.hdrBdr, border: "none", color: "#fff", borderRadius: 6 }}
                placeholder={t("Zoeken…")}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
        )}
        </div>
      </header>

      {/* ── Week nav + Afdeling tabs + Dag selector (lijst-view) ── */}
      {tab === "lijst" && (
        <>
          {/* Week navigatie */}
          <div style={{ background: C.bg2, borderBottom: `1px solid ${C.bdr}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 16px", flexShrink: 0 }}>
            <button onClick={() => setWeekOffset(w => w - 1)} style={{ background: "none", border: `1px solid ${C.bdr}`, color: C.txt2, padding: "4px 12px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>‹</button>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.txt }}>{t("Week {n}", { n: getWeekNr(weekOffset) })}</div>
              <div style={{ fontSize: 11, color: C.txt3 }}>{getWeekLabel(weekOffset)}</div>
            </div>
            <button onClick={() => setWeekOffset(w => w + 1)} style={{ background: "none", border: `1px solid ${C.bdr}`, color: C.txt2, padding: "4px 12px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>›</button>
          </div>

          {/* Dag selector */}
          <div style={{ background: C.bg2, borderBottom: `1px solid ${C.bdr}`, display: "flex", gap: 6, padding: "10px 16px", flexShrink: 0, overflowX: "auto" }}>
            {DAYS.map((d, i) => {
              const isToday = i === (new Date().getDay() === 0 ? 6 : new Date().getDay() - 1) && weekOffset === 0;
              const isActief = activeDag === i;
              const datum = getDayDate(i, weekOffset);
              const dk = dayKey(d, weekOffset);
              // Tel hoeveel items aangevinkt zijn op deze dag
              const gepland = state.items.filter(it => !it.archived && it.afdeling_id === activeAfdId && (it.checks?.[dk] || 0) > 0).length;
              return (
                <button key={d} onClick={() => setActiveDag(i)}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 12px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit", minWidth: 52, background: isActief ? C.hdr : "transparent", color: isActief ? "#fff" : isToday ? C.pri : C.txt2, transition: "background .12s" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>{t(d)}</span>
                  <span style={{ fontSize: 15, fontWeight: 800, marginTop: 2 }}>{datum}</span>
                  {gepland > 0 && <span style={{ width: 6, height: 6, borderRadius: "50%", background: isActief ? "#fff" : C.pri, marginTop: 3 }} />}
                  {gepland === 0 && <span style={{ width: 6, height: 6, marginTop: 3 }} />}
                </button>
              );
            })}
          </div>

          {/* Weergave wissel */}
          <div style={{ background: C.bg2, borderBottom: `1px solid ${C.bdr}`, display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", flexShrink: 0, overflowX: "auto" }}>
            <button onClick={() => setLijstModus("werk")}
              style={{ background: lijstModus === "werk" ? C.hdr : C.bg3, color: lijstModus === "werk" ? "#fff" : C.txt2, border: `1px solid ${lijstModus === "werk" ? C.hdr : C.bdr}`, borderRadius: 7, padding: "6px 12px", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
              {t("Werkoverzicht")}
            </button>
            <button onClick={() => setLijstModus("volledig")}
              style={{ background: lijstModus === "volledig" ? C.hdr : C.bg3, color: lijstModus === "volledig" ? "#fff" : C.txt2, border: `1px solid ${lijstModus === "volledig" ? C.hdr : C.bdr}`, borderRadius: 7, padding: "6px 12px", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
              {t("Volledige lijst")}
            </button>
          </div>

          {lijstModus === "volledig" && (
            <>
              {/* KC_UI_V2_MEP_ACTIONS: beheer direct in de dagelijkse MEP-lijst */}
              <div style={{ background: C.bg2, borderBottom: `1px solid ${C.bdr}`, display: "flex", gap: 0, flexShrink: 0, overflowX: "auto", alignItems: "stretch" }}>
                {afdelingen.map(afd => (
                  <button key={afd.id} onClick={() => setActiveAfdId(afd.id)}
                    style={{ padding: "8px 18px", fontFamily: "inherit", fontSize: 13, fontWeight: activeAfdId === afd.id ? 700 : 400, cursor: "pointer", border: "none", borderBottom: activeAfdId === afd.id ? `2px solid ${C.pri}` : "2px solid transparent", background: "transparent", color: activeAfdId === afd.id ? C.pri : C.txt2, whiteSpace: "nowrap", transition: "all .12s" }}>
                    {afd.naam}
                  </button>
                ))}
              </div>

              {magBeheren && (
                <div style={{ background: C.bg2, borderBottom: `1px solid ${C.bdr}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "9px 12px", flexShrink: 0 }}>
                  <button type="button" onClick={() => setItemPopup({ mode: "new" })}
                    style={{ ...btn(C.pri, "#fff", C.pri), display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                    <Plus size={14} /> {t("Nieuw item")}
                  </button>
                  <div ref={beheerRef} style={{ position: "relative" }}>
                    <button type="button" aria-haspopup="menu" aria-expanded={beheerOpen} onClick={() => setBeheerOpen(open => !open)}
                      style={{ ...btn(C.bg2, C.txt2, C.bdr2), display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                      {t("Beheer")} <ChevronDown size={14} />
                    </button>
                    {beheerOpen && (
                      <div role="menu" style={{ position: "absolute", right: 0, top: "calc(100% + 5px)", zIndex: 30, minWidth: 195, padding: 5, background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 9, boxShadow: "0 8px 24px rgba(15,23,42,.14)" }}>
                        <button type="button" role="menuitem" onClick={async () => {
                          setBeheerOpen(false);
                          if (await requireBeheer("MEP-structuur te beheren")) setStructuurTab("afdelingen");
                        }} style={{ ...btn(C.bg2, C.txt2, "transparent"), display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left" }}>
                          <Building2 size={14} /> {t("Structuur beheren")}
                        </button>
                        <button type="button" role="menuitem" onClick={() => { setBeheerOpen(false); onTabChange?.("archief"); }}
                          style={{ ...btn(C.bg2, C.txt2, "transparent"), display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left" }}>
                          <Archive size={14} /> {t("Archief")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {tab === "archief" && (
        <div style={{ background: C.bg2, borderBottom: `1px solid ${C.bdr}`, display: "flex", alignItems: "center", padding: "9px 12px", flexShrink: 0 }}>
          <button type="button" onClick={() => onTabChange?.("lijst")}
            style={{ ...btn(C.bg2, C.txt2, C.bdr2), display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
            ‹ {t("Terug naar MEP-lijst")}
          </button>
        </div>
      )}

      {coolingError && <p role="status" style={{ margin: 0, padding: 8, color: C.red }}>{t("Terugkoelstatus niet bijgewerkt. Controleer HACCP.")}</p>}
      {/* ── Content ── */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {tab === "lijst" && lijstModus === "werk" && (
          <WerkOverzicht
            state={state}
            weekOffset={weekOffset}
            activeDag={activeDag}
            toggleCheck={toggleCheck}
            onEditItem={item => setItemPopup({ mode: "edit", item })}
            onPrintItem={item => setPrintItem(item)}
            magBeheren={magBeheren}
            onOpenRecept={onOpenRecept} onStartCooling={onStartCooling}
            onAanvullen={(afdId) => { setActiveAfdId(afdId); setLijstModus("volledig"); }}
          />
        )}
        {tab === "lijst" && lijstModus === "volledig" && <LijstView state={state} activeAfdId={activeAfdId} weekOffset={weekOffset} activeDag={activeDag} search={search} openCats={openCats} setOpenCats={setOpenCats} toggleCheck={toggleCheck} onEditItem={item => setItemPopup({ mode: "edit", item })} onPrintItem={item => setPrintItem(item)} magBeheren={magBeheren} onOpenRecept={onOpenRecept} onStartCooling={onStartCooling} />}
        {tab === "archief"   && <ArchiefView state={state} update={update} onEditItem={item => setItemPopup({ mode: "edit", item })} />}
      </div>

      {/* ── Popups ── */}
      {itemPopup && (
        <ItemPopup
          mode={itemPopup.mode}
          item={itemPopup.item}
          state={state}
          activeAfdId={activeAfdId}
          thtNorm={Number(config?.tht_norm_dagen) || 3}
          onSave={async (item) => {
            if (!(await requireBeheer("een MEP-item op te slaan"))) return;
            update(s => ({
              items: itemPopup.mode === "new"
                ? [...s.items, item]
                : s.items.map(i => i.id === item.id ? item : i)
            }));
            setItemPopup(null);
          }}
          onDelete={async (id) => { if (!(await requireBeheer("een MEP-item te verwijderen"))) return; update(s => ({ items: s.items.filter(i => i.id !== id) })); setItemPopup(null); }}
          onArchive={async (id) => { if (!(await requireBeheer("een MEP-item te archiveren"))) return; update(s => ({ items: s.items.map(i => i.id === id ? { ...i, archived: true } : i) })); setItemPopup(null); }}
          onClose={() => setItemPopup(null)}
          onOpenRecept={onOpenRecept} onStartCooling={onStartCooling}
          onAddAfdeling={(naam) => {
            const id = uid();
            update(cur => ({ afdelingen: [...(cur.afdelingen || []), { id, naam }] }));
            setActiveAfdId(id);
            return id;
          }}
          onAddCategorie={(naam, afdeling_id) => {
            const id = uid();
            update(cur => ({ categorieen: [...(cur.categorieen || []), { id, naam, afdeling_id }] }));
            return id;
          }}
          leveranciers={leveranciers}
          medewerkers={medewerkers}
          artikelen={artikelen}
          onthoudArtikel={onthoudArtikel}
          bedrijfsnaam={config?.bedrijfsnaam || ""}
          printUrl={config?.print_server_url || ""}
        />
      )}

      {/* Dagelijkse flow: na afronden direct vragen of een etiket nodig is. */}
      {printVraagItem && (
        <Popup onClose={() => setPrintVraagItem(null)} title={t("Taak afgerond")} breedte={380}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.txt }}>{printVraagItem.naam}</div>
            <div style={{ fontSize: 13, color: C.txt2 }}>{t("Etiket printen?")}</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={() => { const item = printVraagItem; setPrintVraagItem(null); setPrintItem(item); }}
                style={{ ...btn(C.pri, "#fff", C.pri), flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <Printer size={14} /> {t("Print etiket")}
              </button>
              <button type="button" onClick={() => setPrintVraagItem(null)} style={{ ...btn(C.bg3, C.txt2, C.bdr), flex: 1 }}>
                {t("Nee, klaar")}
              </button>
            </div>
          </div>
        </Popup>
      )}

      {/* Etiket printen vanuit de dagelijkse lijst, zonder beheeractie/pincode. */}
      {printItem && (
        <PrintModal
          item={printItem}
          recept={resolveReceptVoorItem(printItem)}
          medewerkers={medewerkers}
          bedrijfsnaam={config?.bedrijfsnaam || ""}
          printUrl={config?.print_server_url || ""}
          autoSluitNaPrint={true}
          onClose={() => setPrintItem(null)}
        />
      )}

      {/* Los etiket: naam intypen die niet als mep-item bestaat, dan printen */}
      {losEtiketOpen && (
        <LosEtiketPopup
          thtNorm={Number(config?.tht_norm_dagen) || 3}
          onClose={() => setLosEtiketOpen(false)}
          onVerder={(losItem) => {
            setLosEtiketOpen(false);
            setPrintItem(losItem); // hergebruikt de bestaande print-flow (PrintModal)
          }}
        />
      )}
      {structuurTab && (
        <Popup onClose={() => setStructuurTab(null)} title={t("MEP-structuur beheren")} breedte={480}>
          <div role="tablist" aria-label={t("MEP-structuur")} style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[["afdelingen", "Afdelingen"], ["categorieen", "Categorieën"]].map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={structuurTab === id} onClick={() => setStructuurTab(id)}
                style={{ ...btn(structuurTab === id ? C.hdr : C.bg3, structuurTab === id ? "#fff" : C.txt2, structuurTab === id ? C.hdr : C.bdr), flex: 1 }}>
                {t(label)}
              </button>
            ))}
          </div>
          {structuurTab === "afdelingen" ? (
            <AfdPopup afdelingen={afdelingen} items={items}
              onSave={(afd) => { update(s => ({ afdelingen: [...s.afdelingen, afd] })); }}
              onRename={(id, naam) => { update(s => ({ afdelingen: s.afdelingen.map(a => a.id === id ? { ...a, naam } : a) })); }}
              onDelete={(id) => { update(s => ({ afdelingen: s.afdelingen.filter(a => a.id !== id), categorieen: s.categorieen.filter(c => c.afdeling_id !== id) })); }}
            />
          ) : (
            <CatPopup categorieen={categorieen} afdelingen={afdelingen} activeAfdId={activeAfdId}
              onSave={(cat) => { update(s => ({ categorieen: [...s.categorieen, cat] })); }}
              onRename={(id, naam) => { update(s => ({ categorieen: s.categorieen.map(c => c.id === id ? { ...c, naam } : c) })); }}
              onDelete={(id) => { update(s => ({ categorieen: s.categorieen.filter(c => c.id !== id), items: s.items.map(i => i.categorie_id === id ? { ...i, categorie_id: null } : i) })); }}
            />
          )}
        </Popup>
      )}

      <WhiteboardSheet
        open={wbOpen}
        onClose={() => setWbOpen(false)}
        items={[...mepWhiteboardItems, ...wb.items]}
        artikelen={wb.artikelen}
        regelMep={regelMepBestelartikel}
        voegToe={wb.voegToe}
        verwijder={wb.verwijder}
      />

      <ModuleWelkom module="mep" />
    </div>
    </CoolingContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LIJST VIEW — dag-weergave zoals BijHENC
// ─────────────────────────────────────────────────────────────────────────────
function WerkOverzicht({ state, weekOffset, activeDag, toggleCheck, onEditItem, onPrintItem, magBeheren, onOpenRecept, onAanvullen, onStartCooling }) {
  const { t } = useT();
  const [klaarOpen, setKlaarOpen] = useState({});
  const dk = dayKey(DAYS[activeDag], weekOffset);
  const afdelingen = state.afdelingen || [];
  const categorieen = state.categorieen || [];
  const catNaam = new Map(categorieen.map(c => [c.id, c.naam]));

  const groepen = afdelingen.map(afd => {
    const items = (state.items || [])
      .filter(it => !it.archived && it.afdeling_id === afd.id && (it.checks?.[dk] || 0) > 0)
      .sort((a, b) => {
        const sa = a.checks?.[dk] || 0;
        const sb = b.checks?.[dk] || 0;
        if (sa !== sb) return sa - sb;
        const ca = catNaam.get(a.categorie_id) || "";
        const cb = catNaam.get(b.categorie_id) || "";
        return ca.localeCompare(cb, "nl") || a.naam.localeCompare(b.naam, "nl");
      });
    const openItems = items.filter(it => (it.checks?.[dk] || 0) === 1);
    const klaarItems = items.filter(it => (it.checks?.[dk] || 0) === 2);
    return { afd, items, openItems, klaarItems, klaar: klaarItems.length, open: openItems.length };
  }).filter(g => g.items.length > 0);

  const totaal = groepen.reduce((n, g) => n + g.items.length, 0);
  const klaarTotaal = groepen.reduce((n, g) => n + g.klaar, 0);
  const openTotaal = groepen.reduce((n, g) => n + g.open, 0);
  const dagLabel = `${t(DAYS[activeDag])} ${getDayDate(activeDag, weekOffset)}`;

  if (totaal === 0) {
    return (
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        <div style={{ maxWidth: 680, margin: "40px auto", background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 12, padding: 24, textAlign: "center", color: C.txt2 }}>
          <ClipboardList size={38} strokeWidth={1.4} color={C.txt3} />
          <h2 style={{ fontSize: 18, color: C.txt, margin: "12px 0 6px" }}>{t("Nog niets gepland")}</h2>
          <p style={{ fontSize: 13, margin: "0 0 16px", lineHeight: 1.5 }}>{t("Vink items aan in de volledige lijst. Daarna verschijnen ze hier automatisch per afdeling.")}</p>
          {afdelingen[0] && (
            <button onClick={() => onAanvullen(afdelingen[0].id)}
              style={{ ...btn(C.pri, "#fff", C.pri), padding: "9px 14px" }}>
              {t("Lijst aanvullen")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.txt }}>{t("Werkoverzicht")} · {dagLabel}</div>
          <div style={{ fontSize: 12, color: C.txt3, marginTop: 2 }}>
            {openTotaal} {t("open")} · {klaarTotaal}/{totaal} {t("klaar")}
          </div>
        </div>
        <button onClick={() => onAanvullen(groepen[0]?.afd?.id)}
          style={{ ...btn(C.bg2, C.txt2, C.bdr), padding: "8px 12px", display: "flex", alignItems: "center", gap: 6 }}>
          <Plus size={14} /> {t("Lijst aanvullen")}
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, alignItems: "start" }}>
        {groepen.map(({ afd, items, klaar, open, openItems, klaarItems }) => {
          const pct = items.length ? (klaar / items.length) * 100 : 0;
          const toonKlaar = klaarItems.length > 0 && (klaarOpen[afd.id] ?? open === 0);
          return (
            <section key={afd.id} style={{ background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ padding: "12px 14px", borderBottom: `1px solid ${C.bdr}`, background: C.bg2 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ fontSize: 15, color: C.txt, margin: 0 }}>{afd.naam}</h3>
                    <div style={{ fontSize: 11, color: C.txt3, marginTop: 2 }}>{open} {t("open")} · {klaar}/{items.length} {t("klaar")}</div>
                  </div>
                  <button onClick={() => onAanvullen(afd.id)}
                    style={{ background: C.priBg, color: C.pri, border: `1px solid ${C.priBd}`, borderRadius: 7, padding: "5px 8px", fontFamily: "inherit", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                    {t("Aanvullen")}
                  </button>
                </div>
                <div style={{ height: 6, background: C.bg3, borderRadius: 999, overflow: "hidden", marginTop: 10 }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: C.green, borderRadius: 999 }} />
                </div>
              </div>
              <div>
                {openItems.map(it => (
                  <ItemRijDag
                    key={it.id}
                    item={it}
                    dk={dk}
                    weekOffset={weekOffset}
                    activeDag={activeDag}
                    toggleCheck={toggleCheck}
                    onEdit={() => onEditItem(it)}
                    onPrint={() => onPrintItem(it)}
                    magBeheren={magBeheren}
                    onOpenRecept={onOpenRecept} onStartCooling={onStartCooling}
                  />
                ))}
                {klaarItems.length > 0 && (
                  <button onClick={() => setKlaarOpen(o => ({ ...o, [afd.id]: !(o[afd.id] ?? open === 0) }))}
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", border: "none", borderTop: openItems.length ? `1px solid ${C.bdr}` : "none", borderBottom: toonKlaar ? `1px solid ${C.bdr}` : "none", background: C.bg3, color: C.txt2, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 700, textAlign: "left" }}>
                    {toonKlaar ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <Check size={14} color={C.green} />
                    <span style={{ flex: 1 }}>{t("Klaar")} ({klaarItems.length})</span>
                  </button>
                )}
                {toonKlaar && klaarItems.map(it => (
                  <ItemRijDag
                    key={it.id}
                    item={it}
                    dk={dk}
                    weekOffset={weekOffset}
                    activeDag={activeDag}
                    toggleCheck={toggleCheck}
                    onEdit={() => onEditItem(it)}
                    onPrint={() => onPrintItem(it)}
                    magBeheren={magBeheren}
                    onOpenRecept={onOpenRecept} onStartCooling={onStartCooling}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function LijstView({ state, activeAfdId, weekOffset, activeDag, search, openCats, setOpenCats, toggleCheck, onEditItem, onPrintItem, magBeheren, onOpenRecept, onStartCooling }) {
  const { t } = useT();
  const { categorieen, items } = state;
  const dk = dayKey(DAYS[activeDag], weekOffset);

  const cats = categorieen.filter(c => c.afdeling_id === activeAfdId);
  const zichtbareItems = items.filter(it =>
    !it.archived &&
    it.afdeling_id === activeAfdId &&
    (!search || it.naam.toLowerCase().includes(search.toLowerCase()))
  );
  const catIds = new Set(cats.map(c => c.id));

  // Teller: hoeveel items nog te doen vandaag (gepland maar niet gedaan)
  const teDoen = zichtbareItems.filter(it => (it.checks?.[dk] || 0) === 1).length;
  // Progressbalk: gedaan / gepland
  const geplandTotaal = zichtbareItems.filter(it => (it.checks?.[dk] || 0) > 0).length;
  const gedaanTotaal  = zichtbareItems.filter(it => (it.checks?.[dk] || 0) === 2).length;
  const progPct = geplandTotaal > 0 ? (gedaanTotaal / geplandTotaal) * 100 : 0;

  if (cats.length === 0 && zichtbareItems.length === 0) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, color: C.txt3 }}>
        <ClipboardList size={40} strokeWidth={1} />
        <p style={{ fontSize: 14 }}>{t("Nog geen items. Voeg hierboven een categorie en item toe.")}</p>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Lijst */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {cats.map(cat => {
          const catItems = zichtbareItems.filter(it => it.categorie_id === cat.id);
          if (catItems.length === 0 && search) return null;
          const open = openCats[cat.id] !== false;
          const catGedaan = catItems.filter(it => (it.checks?.[dk] || 0) === 2).length;
          const catGepland = catItems.filter(it => (it.checks?.[dk] || 0) > 0).length;

          return (
            <div key={cat.id}>
              {/* Categorie header */}
              <div onClick={() => setOpenCats(o => ({ ...o, [cat.id]: !open }))}
                style={{ display: "flex", alignItems: "center", padding: "8px 16px", background: C.bg3, borderBottom: `1px solid ${C.bdr}`, cursor: "pointer", userSelect: "none" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.8, flex: 1 }}>{cat.naam}</span>
                <span style={{ fontSize: 10, color: C.txt3, marginRight: 8 }}>{catGedaan}/{catItems.length}</span>
                {open ? <ChevronDown size={13} color={C.txt3} /> : <ChevronRight size={13} color={C.txt3} />}
              </div>
              {/* Items */}
              {open && catItems.map(it => (
                <ItemRijDag key={it.id} item={it} dk={dk} weekOffset={weekOffset} activeDag={activeDag} toggleCheck={toggleCheck} onEdit={() => onEditItem(it)} onPrint={() => onPrintItem(it)} magBeheren={magBeheren} onOpenRecept={onOpenRecept} onStartCooling={onStartCooling} />
              ))}
            </div>
          );
        })}

        {/* Items zonder categorie */}
        {zichtbareItems.filter(it => !it.categorie_id || !catIds.has(it.categorie_id)).map(it => (
          <ItemRijDag key={it.id} item={it} dk={dk} weekOffset={weekOffset} activeDag={activeDag} toggleCheck={toggleCheck} onEdit={() => onEditItem(it)} onPrint={() => onPrintItem(it)} magBeheren={magBeheren} onOpenRecept={onOpenRecept} onStartCooling={onStartCooling} />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ITEM RIJ — dag-weergave
// ─────────────────────────────────────────────────────────────────────────────
function ItemRijDag({ item, dk, weekOffset, activeDag, toggleCheck, onEdit, onPrint, magBeheren = false, onOpenRecept, onStartCooling }) {
  const { t } = useT();
  const coolingActive = useContext(CoolingContext).has(String(item.id));
  const st = item.checks?.[dk] || 0;
  const gedaan = st === 2;
  const gepland = st === 1;

  const schuifVooruit = (e) => {
    e.stopPropagation();
    const volgendeIdx = (activeDag + 1) % 7;
    const volgendeDk = dayKey(DAYS[volgendeIdx], volgendeIdx < activeDag ? weekOffset + 1 : weekOffset);
    toggleCheck(item.id, dk, { geenPrintVraag: true });
    setTimeout(() => {
      toggleCheck(item.id, volgendeDk, { geenPrintVraag: true });
    }, 10);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", padding: "10px 16px", borderBottom: `1px solid ${C.bdr}`, background: C.bg2, gap: 12 }}>
      <button onClick={() => toggleCheck(item.id, dk)}
        style={{
          width: 28, height: 28, borderRadius: 6, flexShrink: 0,
          border: `2px solid ${st === 0 ? C.bdr2 : gepland ? C.amber : C.green}`,
          background: st === 0 ? "transparent" : gepland ? C.amberBg : C.greenBg,
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 15, fontWeight: 800, color: gepland ? C.amber : C.green,
          transition: "all .1s", padding: 0,
        }}>
        {st === 0 ? null : gepland ? <X size={15} /> : <Check size={15} />}
      </button>

      <div style={{ flex: 1, cursor: "pointer", minWidth: 0 }} onClick={onPrint} title={t("Etiket printen")}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{
            fontSize: 13, fontWeight: 500,
            color: gedaan ? C.txt3 : C.txt,
            textDecoration: gedaan ? "line-through" : "none",
          }}>{item.naam}</span>
          {item.recept_id && onOpenRecept && (
            <span
              onClick={e => { e.stopPropagation(); onOpenRecept(item.recept_id); }}
              style={{ fontSize: 10, background: C.priBg, color: C.pri, border: `1px solid ${C.priBd}`, padding: "1px 7px", borderRadius: 10, fontWeight: 600, cursor: "pointer" }}>
              {t("RECEPT")}
            </span>
          )}
          {item.recept_id && !onOpenRecept && (
            <span style={{ fontSize: 10, background: C.priBg, color: C.pri, border: `1px solid ${C.priBd}`, padding: "1px 7px", borderRadius: 10, fontWeight: 600 }}>
              {t("RECEPT")}
            </span>
          )}
          {item.bestelartikelen?.length > 0 && (
            <span style={{ fontSize: 10, color: C.txt3, display: "flex", alignItems: "center", gap: 2 }}>
              <Package size={9} />{item.bestelartikelen.length}
            </span>
          )}
        </div>
        {item.notitie && !gedaan && (
          <div style={{ fontSize: 11, color: C.txt3, marginTop: 2, fontStyle: "italic" }}>{item.notitie}</div>
        )}
      </div>

      {(item.terugkoelproduct || coolingActive) && onStartCooling && (
        <button type="button" className={coolingActive ? "kc-cooling-active" : undefined} title={t(coolingActive ? "Terugkoeling loopt — open HACCP" : "Terugkoeling starten")} aria-label={t(coolingActive ? "Terugkoeling loopt — open HACCP" : "Terugkoeling starten") + ": " + item.naam}
          onClick={e => { e.stopPropagation(); onStartCooling(item, coolingActive); }}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, flexShrink: 0, borderRadius: 8, border: `1px solid ${coolingActive ? C.red : C.priBd}`, color: coolingActive ? C.red : C.pri, background: coolingActive ? C.redBg : C.priBg, cursor: "pointer" }}>
          <Thermometer size={20} />
        </button>
      )}
      {st > 0 && (
        <button onClick={schuifVooruit} title={t("Naar morgen")}
          style={{ background: C.bg3, border: `1px solid ${C.bdr}`, color: C.txt2, borderRadius: 7, cursor: "pointer", padding: "5px 8px", display: "flex", alignItems: "center", gap: 4, flexShrink: 0, fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>
          <ChevronRight size={12} /> {t("Morgen")}
        </button>
      )}

      {magBeheren && (
        <button onClick={(e) => { e.stopPropagation(); onEdit(); }} title={t("Item bewerken")} aria-label={t("Item bewerken")}
          style={{ background: C.bg2, border: `1px solid ${C.bdr}`, color: C.txt2, borderRadius: 7, cursor: "pointer", padding: 6, display: "flex", alignItems: "center", flexShrink: 0 }}>
          <Pencil size={14} />
        </button>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
// ARCHIEF VIEW
// ─────────────────────────────────────────────────────────────────────────────
function ArchiefView({ state, update, onEditItem }) {
  const { t } = useT();
  const gearchiveerd = state.items.filter(i => i.archived);

  const herstel = (id) => update(s => ({ items: s.items.map(i => i.id === id ? { ...i, archived: false } : i) }));
  const verwijder = (id) => { if (confirm(t("Item definitief verwijderen?"))) update(s => ({ items: s.items.filter(i => i.id !== id) })); };

  if (gearchiveerd.length === 0) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, color: C.txt3 }}>
        <Archive size={40} strokeWidth={1} />
        <p style={{ fontSize: 14 }}>{t("Geen gearchiveerde items.")}</p>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
        {t("Archief")} ({gearchiveerd.length})
      </div>
      {gearchiveerd.map(item => (
        <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 6, marginBottom: 4 }}>
          <div style={{ flex: 1, cursor: "pointer" }} onClick={() => onEditItem(item)}>
            <div style={{ fontSize: 13, fontWeight: 500, color: C.txt2, textDecoration: "line-through" }}>{item.naam}</div>
            {item.notitie && <div style={{ fontSize: 11, color: C.txt3, marginTop: 2 }}>{item.notitie}</div>}
          </div>
          <button onClick={() => herstel(item.id)} style={{ ...btn(C.greenBg, C.green, C.greenBd), fontSize: 11 }}>{t("Herstellen")}</button>
          <button onClick={() => verwijder(item.id)} style={{ ...btn(C.redBg, C.red, C.redBd), padding: "7px 10px" }}><Trash2 size={13} /></button>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ITEM POPUP
// ─────────────────────────────────────────────────────────────────────────────
const ALLERGENEN_LIJST = [
  "Gluten","Schaaldieren","Eieren","Vis","Pindas","Soja","Melk","Noten",
  "Selderij","Mosterd","Sesamzaad","Zwaveldioxide","Lupine","Weekdieren",
];
const GLUTEN_SUB = ["Tarwe", "Rogge", "Gerst", "Haver"];
const NOTEN_SUB = ["Amandelen", "Hazelnoten", "Walnoten", "Cashewnoten", "Pecannoten", "Paranoten", "Pistachenoten", "Macadamianoten"];
const BEWAARADVIES_OPTIES = ["", "Bewaren onder 7°C", "Bewaren onder -18°C", "Bewaren op kamertemperatuur", "Direct serveren"];

function InklapSectie({ titel, status, open, onToggle, children }) {
  const { t } = useT();
  return (
    <div style={{ border: `1px solid ${C.bdr}`, borderRadius: 10, background: C.bg2, overflow: "hidden" }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: "100%",
          minHeight: 48,
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "11px 12px",
          background: open ? C.bg3 : C.bg2,
          border: "none",
          borderBottom: open ? `1px solid ${C.bdr}` : "none",
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
        }}
      >
        <ChevronRight size={16} color={C.txt2} style={{ flexShrink: 0, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .12s" }} />
        <span style={{ flex: 1, fontSize: 12.5, fontWeight: 800, color: C.txt, textTransform: "uppercase", letterSpacing: 0.7 }}>{t(titel)}</span>
        {status && <span style={{ fontSize: 11.5, color: C.txt3, fontWeight: 600, whiteSpace: "nowrap" }}>{status}</span>}
      </button>
      {open && <div style={{ padding: 12 }}>{children}</div>}
    </div>
  );
}

function ItemPopup({ mode, item, state, activeAfdId, onSave, onDelete, onArchive, onClose, onOpenRecept, onAddAfdeling, onAddCategorie, leveranciers = [], medewerkers = [], artikelen = [], onthoudArtikel, thtNorm = 3, bedrijfsnaam = "", printUrl }) {
  // KC_UI_V2_MEP_INLINE_STRUCTUUR
  const { t } = useT();
  const leeg = {
    id: uid(), naam: "", afdeling_id: activeAfdId, categorie_id: "",
    notitie: "", bestelartikelen: [], recept_id: null, checks: {}, archived: false, thtDagen: thtNorm, terugkoelproduct: false,
  };
  const [form, setForm] = useState(item ? { ...item } : leeg);
  const [nieuwBa, setNieuwBa] = useState({ naam: "", hoeveelheid: "", eenheid: "kg", leverancier: "" });
  const [baFout, setBaFout] = useState(false);
  const [editBaId, setEditBaId] = useState(null); // id van het bestelartikel dat bewerkt wordt
  const [toonBaSug, setToonBaSug] = useState(false); // suggestie-dropdown onder het naamveld
  const [baSugIdx, setBaSugIdx] = useState(-1); // gemarkeerde suggestie voor toetsenbord
  const naamRef = useRef();
  const [openSecties, setOpenSecties] = useState(() => ({
    recept: !!item?.recept_id,
    allergenen: (item?.allergenen || []).length > 0,
    houdbaarheid: !!item?.bewaaradvies || item?.thtDagen !== undefined,
    bestel: (item?.bestelartikelen || []).length > 0,
  }));

  // Laad recepten uit ReceptenModule localStorage
  const recepten = (() => {
    try {
      const s = localStorage.getItem("kc-recepten-v1");
      if (s) return JSON.parse(s)?.recepten?.filter(r => !r.archived) || [];
    } catch {}
    return [];
  })();

  const [printOpen, setPrintOpen] = useState(false);

  useEffect(() => { naamRef.current?.focus(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const gekoppeldRecept = recepten.find(r => r.id === form.recept_id);
  const toggleSectie = (naam) => setOpenSecties(s => ({ ...s, [naam]: !s[naam] }));
  const allergenenAantal = (form.allergenen || []).filter(a => ![...GLUTEN_SUB, ...NOTEN_SUB].includes(a)).length;
  const bestelAantal = (form.bestelartikelen || []).length;

  // Suggesties uit de gedeelde artikellijst, gefilterd op wat je typt
  const baSugTreffers = (() => {
    const q = nieuwBa.naam.trim().toLowerCase();
    if (!q) return [];
    return (artikelen || []).filter(a => a.toLowerCase().includes(q)).slice(0, 5);
  })();

  const addBa = () => {
    if (!nieuwBa.naam.trim()) { setBaFout(true); return; }
    const schoon = { ...nieuwBa, naam: nieuwBa.naam.trim() };
    setForm(f => {
      const lijst = f.bestelartikelen || [];
      // Bewerken? Bestaand artikel bijwerken (id behouden). Anders nieuw toevoegen.
      if (editBaId) return { ...f, bestelartikelen: lijst.map(b => b.id === editBaId ? { ...b, ...schoon } : b) };
      return { ...f, bestelartikelen: [...lijst, { id: uid(), ...schoon }] };
    });
    if (!editBaId) onthoudArtikel?.(schoon.naam); // nieuw artikel onthouden voor toekomstige suggesties
    setNieuwBa({ naam: "", hoeveelheid: "", eenheid: "kg", leverancier: "" });
    setEditBaId(null);
    setBaFout(false);
    setToonBaSug(false);
    setBaSugIdx(-1);
  };
  const startEditBa = (ba) => {
    setNieuwBa({ naam: ba.naam || "", hoeveelheid: ba.hoeveelheid ?? "", eenheid: ba.eenheid || "kg", leverancier: ba.leverancier || "" });
    setEditBaId(ba.id);
    setBaFout(false);
  };
  const annuleerBewerken = () => {
    setNieuwBa({ naam: "", hoeveelheid: "", eenheid: "kg", leverancier: "" });
    setEditBaId(null);
    setBaFout(false);
  };
  const delBa = (id) => {
    setForm(f => ({ ...f, bestelartikelen: f.bestelartikelen.filter(b => b.id !== id) }));
    if (editBaId === id) annuleerBewerken();
  };

  const opslaan = () => {
    if (!form.naam.trim()) return naamRef.current?.focus();
    onSave(form);
  };

  const catOptions = state.categorieen.filter(c => c.afdeling_id === (form.afdeling_id || activeAfdId));

  const nieuweAfdeling = () => {
    const naam = window.prompt(t("Naam van de nieuwe afdeling"));
    const n = String(naam || "").trim();
    if (!n) return;
    const bestaand = state.afdelingen.find(a => String(a.naam || "").trim().toLowerCase() === n.toLowerCase());
    if (bestaand) {
      setForm(f => ({ ...f, afdeling_id: bestaand.id, categorie_id: "" }));
      return;
    }
    const id = onAddAfdeling?.(n);
    if (id) setForm(f => ({ ...f, afdeling_id: id, categorie_id: "" }));
  };

  const nieuweCategorie = () => {
    const afdId = form.afdeling_id || activeAfdId;
    if (!afdId) { window.alert(t("Kies eerst een afdeling.")); return; }
    const naam = window.prompt(t("Naam van de nieuwe categorie"));
    const n = String(naam || "").trim();
    if (!n) return;
    const bestaand = state.categorieen.find(c => c.afdeling_id === afdId && String(c.naam || "").trim().toLowerCase() === n.toLowerCase());
    if (bestaand) { set("categorie_id", bestaand.id); return; }
    const id = onAddCategorie?.(n, afdId);
    if (id) set("categorie_id", id);
  };

  return (
    <Popup onClose={onClose} title={mode === "new" ? t("Nieuw item") : t("Item bewerken")} breedte={540}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

        {mode === "new" && (
          <Hint id="mep-nieuwitem" tekst={t("Geef je item een naam en kies een categorie. Koppel eventueel een recept, of voeg bestelartikelen toe — die verschijnen automatisch op het Whiteboard.")} />
        )}

        {/* Naam */}
        <Veld label={t("Naam *")}>
          <input ref={naamRef} style={inp} placeholder={t("Naam van het item")} value={form.naam} onChange={e => set("naam", e.target.value)} onKeyDown={e => e.key === "Enter" && opslaan()} />
        </Veld>

        {/* Afdeling + Categorie: structuur aanmaken waar je die gebruikt */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Veld label={t("Afdeling")}>
            <select style={inp} value={form.afdeling_id || ""} onChange={e => setForm(f => ({ ...f, afdeling_id: e.target.value, categorie_id: "" }))}>
              <option value="">{t("Kies…")}</option>
              {state.afdelingen.map(a => <option key={a.id} value={a.id}>{a.naam}</option>)}
            </select>
            <button type="button" onClick={nieuweAfdeling}
              style={{ marginTop: 6, padding: 0, border: 0, background: "transparent", color: C.pri, fontFamily: "inherit", fontSize: 11.5, fontWeight: 750, cursor: "pointer" }}>
              + {t("Nieuwe afdeling")}
            </button>
          </Veld>
          <Veld label={t("Categorie")}>
            <select style={inp} value={form.categorie_id || ""} onChange={e => set("categorie_id", e.target.value)}>
              <option value="">{t("Geen")}</option>
              {catOptions.map(c => <option key={c.id} value={c.id}>{c.naam}</option>)}
            </select>
            <button type="button" onClick={nieuweCategorie}
              style={{ marginTop: 6, padding: 0, border: 0, background: "transparent", color: C.pri, fontFamily: "inherit", fontSize: 11.5, fontWeight: 750, cursor: "pointer" }}>
              + {t("Nieuwe categorie")}
            </button>
          </Veld>
        </div>

        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: 12, border: `1px solid ${C.priBd}`, borderRadius: 9, background: C.priBg, cursor: "pointer" }}>
          <input type="checkbox" checked={!!form.terugkoelproduct} onChange={e => set("terugkoelproduct", e.target.checked)} style={{ width: 20, height: 20, flexShrink: 0 }} />
          <span><strong>{t("Dit product moet worden teruggekoeld")}</strong><span style={{ display: "block", marginTop: 4, fontSize: 12, color: C.txt2 }}>{t("Start zelf een meting via de thermometer in de lijst. Afvinken start geen terugkoeling.")}</span></span>
        </label>

        {/* Notitie */}
        <Veld label={t("Notitie")}>
          <textarea style={{ ...inp, minHeight: 60, resize: "vertical" }} placeholder={t("Optionele notitie…")} value={form.notitie || ""} onChange={e => set("notitie", e.target.value)} />
        </Veld>

        {/* Recept koppeling */}
        <InklapSectie
          titel="Recept koppelen"
          status={gekoppeldRecept ? gekoppeldRecept.naam : t("geen recept")}
          open={openSecties.recept}
          onToggle={() => toggleSectie("recept")}
        >
        <Veld label={t("Recept")}>
          {recepten.length === 0 ? (
            <div style={{ ...inp, color: C.txt3, fontSize: 12, display: "flex", alignItems: "center" }}>
              {t("Nog geen recepten — voeg ze toe in Receptenbeheer")}
            </div>
          ) : (
            <div>
              <select style={{ ...inp, color: form.recept_id ? C.txt : C.txt3 }}
                value={form.recept_id || ""}
                onChange={e => set("recept_id", e.target.value || null)}>
                <option value="">{t("— Geen recept gekoppeld —")}</option>
                {recepten.sort((a,b) => a.naam.localeCompare(b.naam,"nl")).map(r => (
                  <option key={r.id} value={r.id}>{r.naam}</option>
                ))}
              </select>
              {gekoppeldRecept && (
                <div style={{ marginTop: 6, padding: "6px 10px", background: C.priBg, border: `1px solid ${C.priBd}`, borderRadius: 6, fontSize: 11, color: C.pri, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ flex: 1 }}>
                    📖 {gekoppeldRecept.naam}
                    {gekoppeldRecept.allergenen?.length > 0 && (
                      <span style={{ color: C.red, marginLeft: 8 }}>⚠ {gekoppeldRecept.allergenen.filter(a => !["Tarwe","Rogge","Gerst","Haver","Amandelen","Hazelnoten","Walnoten","Cashewnoten","Pecannoten","Paranoten","Pistachenoten","Macadamianoten"].includes(a)).map(a => t(a)).join(", ")}</span>
                    )}
                  </span>
                  {onOpenRecept && (
                    <button onClick={() => { onClose(); onOpenRecept(gekoppeldRecept.id); }}
                      style={{ ...btn(C.pri, "#fff", C.pri), padding: "4px 10px", fontSize: 11 }}>
                      {t("Bekijk recept →")}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </Veld>
        </InklapSectie>

        {/* Allergenen — handmatig in te vullen wanneer er geen recept gekoppeld is */}
        {!form.recept_id && (
          <>
          <InklapSectie
            titel="Allergenen"
            status={allergenenAantal ? t("{n} gekozen", { n: allergenenAantal }) : t("geen gekozen")}
            open={openSecties.allergenen}
            onToggle={() => toggleSectie("allergenen")}
          >
          <Veld label={t("Allergenen")}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {ALLERGENEN_LIJST.map(a => {
                const aan = (form.allergenen || []).includes(a);
                return (
                  <button key={a} type="button"
                    onClick={() => set("allergenen", aan ? (form.allergenen || []).filter(x => x !== a) : [...(form.allergenen || []), a])}
                    style={{ padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: aan ? 700 : 500, border: aan ? `1.5px solid ${C.red}` : `1px solid ${C.bdr}`, background: aan ? C.redBg : C.bg2, color: aan ? C.red : C.txt }}>
                    {t(a)}
                  </button>
                );
              })}
            </div>

            {/* Gluten — graansoort, alleen als Gluten actief */}
            {(form.allergenen || []).includes("Gluten") && (
              <div style={{ padding: "10px 12px", background: C.bg3, borderRadius: 8, marginTop: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>{t("Gluten — specificeer graansoort")}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {GLUTEN_SUB.map(sub => {
                    const aan = (form.allergenen || []).includes(sub);
                    return (
                      <button key={sub} type="button"
                        onClick={() => set("allergenen", aan ? (form.allergenen || []).filter(x => x !== sub) : [...(form.allergenen || []), sub])}
                        style={{ padding: "4px 10px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: aan ? 700 : 500, border: aan ? `1.5px solid ${C.red}` : `1px solid ${C.bdr2}`, background: aan ? C.redBg : C.bg2, color: aan ? C.red : C.txt2 }}>
                        {t(sub)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Noten — soort, alleen als Noten actief */}
            {(form.allergenen || []).includes("Noten") && (
              <div style={{ padding: "10px 12px", background: C.bg3, borderRadius: 8, marginTop: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>{t("Noten — specificeer soort")}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {NOTEN_SUB.map(sub => {
                    const aan = (form.allergenen || []).includes(sub);
                    return (
                      <button key={sub} type="button"
                        onClick={() => set("allergenen", aan ? (form.allergenen || []).filter(x => x !== sub) : [...(form.allergenen || []), sub])}
                        style={{ padding: "4px 10px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: aan ? 700 : 500, border: aan ? `1.5px solid ${C.red}` : `1px solid ${C.bdr2}`, background: aan ? C.redBg : C.bg2, color: aan ? C.red : C.txt2 }}>
                        {t(sub)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </Veld>
          </InklapSectie>

          <InklapSectie
            titel="Houdbaarheid"
            status={form.bewaaradvies ? t(form.bewaaradvies) : `${form.thtDagen ?? thtNorm} ${t("dagen")}`}
            open={openSecties.houdbaarheid}
            onToggle={() => toggleSectie("houdbaarheid")}
          >
          <Veld label={t("Bewaaradvies")}>
            <select style={inp} value={form.bewaaradvies || ""} onChange={e => set("bewaaradvies", e.target.value)}>
              {BEWAARADVIES_OPTIES.map(o => <option key={o} value={o}>{o ? t(o) : t("— geen —")}</option>)}
            </select>
          </Veld>
          <Veld label={t("Bewaartermijn (dagen)")}>
            <input type="number" style={inp} min={0} max={365} placeholder={t("bijv. {n}", { n: thtNorm })}
              value={form.thtDagen ?? ""} onChange={e => set("thtDagen", e.target.value)} />
            <div style={{ marginTop: 6, background: C.bg3, border: `1px solid ${C.bdr}`, borderRadius: 8, padding: "8px 11px", fontSize: 12, color: C.txt2, lineHeight: 1.5 }}>
              <strong style={{ color: C.txt2 }}>{t("Richtlijn houdbaarheid:")}</strong> {t("2 dagen onder 7 °C · 3 dagen onder 4 °C")}
            </div>
          </Veld>
          </InklapSectie>
          </>
        )}

        {/* Bestelartikelen */}
        <InklapSectie
          titel="Bestelartikelen"
          status={bestelAantal ? t("{n} artikel(en)", { n: bestelAantal }) : t("geen bestelartikelen")}
          open={openSecties.bestel}
          onToggle={() => toggleSectie("bestel")}
        >
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
            {t("Bestelartikelen")}
          </div>
          {(form.bestelartikelen || []).length === 0 && (
            <div style={{ fontSize: 12, color: C.txt3, fontStyle: "italic", marginBottom: 8 }}>{t("Nog geen bestelartikelen.")}</div>
          )}
          {(form.bestelartikelen || []).map(ba => (
            <div key={ba.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", background: editBaId === ba.id ? C.priBg : C.bg3, border: `1px solid ${editBaId === ba.id ? C.priBd : "transparent"}`, borderRadius: 6, marginBottom: 4 }}>
              <span style={{ flex: 1, fontSize: 13, color: C.txt }}>{ba.naam}</span>
              {ba.hoeveelheid && <span style={{ fontSize: 12, color: C.txt2 }}>{ba.hoeveelheid} {t(ba.eenheid)}</span>}
              {ba.leverancier && <span style={{ fontSize: 11, color: C.txt3 }}>{ba.leverancier}</span>}
              <button onClick={() => startEditBa(ba)} title={t("Bewerken")} style={{ background: "none", border: "none", cursor: "pointer", color: C.pri, padding: 2, display: "flex" }}><Pencil size={13} /></button>
              <button onClick={() => delBa(ba.id)} title={t("Verwijderen")} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, padding: 2, display: "flex" }}><Trash2 size={13} /></button>
            </div>
          ))}

          {/* Nieuw bestelartikel invoer — naam op een eigen, volledige regel zodat
              hij groot genoeg is om te typen én te bewerken (was te smal op mobiel,
              waardoor de cursor terugsprong en tekst achterstevoren verscheen).
              Autocorrectie/voorspeltekst staan uit om dat caret-gedrag te voorkomen. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            <div style={{ position: "relative" }}>
              <input
                style={{ ...inp, ...(baFout ? { borderColor: C.red } : {}) }}
                placeholder={t("Artikel naam *")}
                value={nieuwBa.naam}
                onChange={e => { setNieuwBa(f => ({ ...f, naam: e.target.value })); setBaFout(false); setToonBaSug(true); setBaSugIdx(-1); }}
                onFocus={() => setToonBaSug(true)}
                onBlur={() => setTimeout(() => setToonBaSug(false), 120)}
                onKeyDown={e => {
                  if (e.key === "ArrowDown") { e.preventDefault(); if (baSugTreffers.length) { setToonBaSug(true); setBaSugIdx(i => (i + 1) % baSugTreffers.length); } }
                  else if (e.key === "ArrowUp") { e.preventDefault(); if (baSugTreffers.length) { setToonBaSug(true); setBaSugIdx(i => (i <= 0 ? baSugTreffers.length - 1 : i - 1)); } }
                  else if (e.key === "Enter") {
                    if (toonBaSug && baSugIdx >= 0 && baSugTreffers[baSugIdx]) { e.preventDefault(); setNieuwBa(f => ({ ...f, naam: baSugTreffers[baSugIdx] })); setToonBaSug(false); setBaSugIdx(-1); }
                    else addBa();
                  }
                  else if (e.key === "Escape") { if (toonBaSug && baSugTreffers.length) { e.stopPropagation(); setToonBaSug(false); setBaSugIdx(-1); } }
                }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
              {toonBaSug && baSugTreffers.length > 0 && (
                <div style={{ position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)", zIndex: 20, background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(15,23,42,.12)", overflow: "hidden" }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase", color: C.txt3, padding: "8px 12px 4px" }}>{t("Onthouden artikelen")}</div>
                  {baSugTreffers.map((a, i) => (
                    <div key={a} onMouseDown={() => { setNieuwBa(f => ({ ...f, naam: a })); setToonBaSug(false); setBaSugIdx(-1); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", cursor: "pointer", background: i === baSugIdx ? C.bg3 : "transparent" }}
                      onMouseEnter={() => setBaSugIdx(i)}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.pri, flex: "none" }} />
                      <span style={{ flex: 1, fontSize: 13, color: C.txt }}>{a}</span>
                      <span style={{ fontSize: 10.5, color: C.pri, background: "#EFF6FF", borderRadius: 999, padding: "2px 8px", fontWeight: 600 }}>{t("bestaand")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "80px 84px 1fr auto", gap: 6, alignItems: "center" }}>
              <input style={inp} placeholder={t("Hoev.")} type="number" step="0.1" inputMode="decimal" value={nieuwBa.hoeveelheid} onChange={e => setNieuwBa(f => ({ ...f, hoeveelheid: e.target.value }))} />
              <select style={inp} value={nieuwBa.eenheid} onChange={e => setNieuwBa(f => ({ ...f, eenheid: e.target.value }))}>
                {UNITS.map(u => <option key={u} value={u}>{t(u)}</option>)}
              </select>
              <input style={inp} placeholder={t("Leverancier")} list="kc-ba-leveranciers" autoComplete="off" value={nieuwBa.leverancier} onChange={e => setNieuwBa(f => ({ ...f, leverancier: e.target.value }))} />
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={addBa} title={editBaId ? t("Bijwerken") : t("Toevoegen")} style={{ ...btn(C.pri, "#fff", C.pri), padding: "7px 10px", display: "flex", alignItems: "center" }}>{editBaId ? <Check size={15} /> : <Plus size={15} />}</button>
                {editBaId && (
                  <button onClick={annuleerBewerken} title={t("Annuleren")} style={{ ...btn(C.bg3, C.txt2, C.bdr), padding: "7px 10px", display: "flex", alignItems: "center" }}><X size={15} /></button>
                )}
              </div>
            </div>
          </div>
          {leveranciers.length > 0 && (
            <datalist id="kc-ba-leveranciers">
              {leveranciers.map(l => <option key={l} value={l} />)}
            </datalist>
          )}
        </div>
        </InklapSectie>

        {/* Knoppen */}
        <div style={{ display: "flex", gap: 8, marginTop: 4, paddingTop: 14, borderTop: `1px solid ${C.bdr}` }}>
          <button onClick={opslaan} style={{ ...btn(C.pri, "#fff", C.pri), flex: 1 }}>
            💾 {t("Opslaan")}
          </button>
          <button onClick={() => setPrintOpen(true)} style={{ ...btn(C.bg3, C.txt2, C.bdr), gap: 5 }}>
            <Printer size={13} /> {t("Etiket")}
          </button>
          {mode === "edit" && (
            <>
              <button onClick={() => onArchive(form.id)} style={btn(C.bg3, C.txt2, C.bdr)}>
                <Archive size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />{t("Archief")}
              </button>
              <button onClick={() => { if (confirm(t("Item verwijderen?"))) onDelete(form.id); }} style={btn(C.redBg, C.red, C.redBd)}>
                <Trash2 size={13} />
              </button>
            </>
          )}
        </div>

        {/* Print modal */}
        {printOpen && (
          <PrintModal
            item={form}
            recept={gekoppeldRecept}
            medewerkers={medewerkers}
            bedrijfsnaam={bedrijfsnaam}
            printUrl={printUrl}
            autoSluitNaPrint={false}
            onKlaar={() => { setPrintOpen(false); onClose(); }}
            onClose={() => setPrintOpen(false)}
          />
        )}
      </div>
    </Popup>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AFDELING POPUP
// ─────────────────────────────────────────────────────────────────────────────
function AfdPopup({ afdelingen, items = [], onSave, onRename, onDelete }) {
  const { t } = useT();
  const [nieuw, setNieuw] = useState("");
  const [edit, setEdit] = useState(null); // { id, naam }

  const toevoegen = () => {
    if (!nieuw.trim()) return;
    onSave({ id: uid(), naam: nieuw.trim() });
    setNieuw("");
  };
  const opslaan = () => {
    if (!edit?.naam.trim()) return;
    onRename(edit.id, edit.naam.trim());
    setEdit(null);
  };

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {afdelingen.map(a => {
          const aantalItems = items.filter(i => i.afdeling_id === a.id).length;
          return (
          <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {edit?.id === a.id ? (
              <>
                <input style={{ ...inp, flex: 1 }} value={edit.naam} onChange={e => setEdit(f => ({ ...f, naam: e.target.value }))} autoFocus onKeyDown={e => e.key === "Enter" && opslaan()} />
                <button onClick={opslaan} style={btn(C.pri, "#fff", C.pri)}>OK</button>
                <button onClick={() => setEdit(null)} style={btn(C.bg3, C.txt2, C.bdr)}>✕</button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontSize: 13, color: C.txt }}>
                  {a.naam}
                  {aantalItems > 0 && <span style={{ marginLeft: 7, color: C.txt3, fontSize: 11 }}>({aantalItems} {aantalItems === 1 ? t("item") : t("items")})</span>}
                </span>
                <button onClick={() => setEdit({ ...a })} style={btn(C.bg3, C.txt2, C.bdr)}>Bewerk</button>
                <button
                  onClick={() => {
                    if (aantalItems > 0) {
                      alert(t("Deze afdeling bevat nog MEP-items. Verplaats of verwijder deze items eerst voordat je de afdeling verwijdert."));
                      return;
                    }
                    if (confirm(t('Afdeling "{naam}" en alle bijbehorende categorieën verwijderen?', { naam: a.naam }))) onDelete(a.id);
                  }}
                  title={aantalItems > 0 ? t("Verwijderen kan pas als de afdeling geen MEP-items meer bevat.") : t("Afdeling verwijderen")}
                  style={btn(aantalItems > 0 ? C.bg3 : C.redBg, aantalItems > 0 ? C.txt3 : C.red, aantalItems > 0 ? C.bdr : C.redBd)}
                ><Trash2 size={13} /></button>
              </>
            )}
          </div>
          );
        })}
        <div style={{ display: "flex", gap: 8, marginTop: 8, paddingTop: 12, borderTop: `1px solid ${C.bdr}` }}>
          <input style={{ ...inp, flex: 1 }} placeholder={t("Nieuwe afdeling…")} value={nieuw} onChange={e => setNieuw(e.target.value)} onKeyDown={e => e.key === "Enter" && toevoegen()} />
          <button onClick={toevoegen} style={btn(C.pri, "#fff", C.pri)}>+ {t("Toevoegen")}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CATEGORIE POPUP
// ─────────────────────────────────────────────────────────────────────────────
function CatPopup({ categorieen, afdelingen, activeAfdId, onSave, onRename, onDelete }) {
  const { t } = useT();
  const [nieuw, setNieuw] = useState("");
  const [selAfd, setSelAfd] = useState(activeAfdId || afdelingen[0]?.id || "");
  const [edit, setEdit] = useState(null);

  // Corrigeer de gekozen afdeling zodra er een geldige beschikbaar is. Bij een
  // verse gebruiker is activeAfdId bij de eerste render soms nog niet gezet
  // (die volgt uit een effect in MepModule). Stond selAfd daardoor op leeg of
  // op een afdeling die niet (meer) bestaat, dan zou de lijst onterecht "Nog
  // geen categorieën" tonen tot je het venster opnieuw opende. Dit trekt 'm
  // meteen recht, zonder een bewuste afdelingskeuze te overschrijven.
  useEffect(() => {
    const geldig = selAfd && afdelingen.some(a => a.id === selAfd);
    if (!geldig) {
      const doel = (activeAfdId && afdelingen.some(a => a.id === activeAfdId))
        ? activeAfdId
        : afdelingen[0]?.id || "";
      if (doel && doel !== selAfd) setSelAfd(doel);
    }
  }, [activeAfdId, afdelingen, selAfd]);

  const gefilterd = categorieen.filter(c => c.afdeling_id === selAfd);

  const toevoegen = () => {
    if (!nieuw.trim() || !selAfd) return;
    onSave({ id: uid(), naam: nieuw.trim(), afdeling_id: selAfd });
    setNieuw("");
  };
  const opslaan = () => {
    if (!edit?.naam.trim()) return;
    onRename(edit.id, edit.naam.trim());
    setEdit(null);
  };

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {afdelingen.length > 1 && (
          <Veld label={t("Afdeling")}>
            <select style={inp} value={selAfd} onChange={e => setSelAfd(e.target.value)}>
              {afdelingen.map(a => <option key={a.id} value={a.id}>{a.naam}</option>)}
            </select>
          </Veld>
        )}
        {gefilterd.map(c => (
          <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {edit?.id === c.id ? (
              <>
                <input style={{ ...inp, flex: 1 }} value={edit.naam} onChange={e => setEdit(f => ({ ...f, naam: e.target.value }))} autoFocus onKeyDown={e => e.key === "Enter" && opslaan()} />
                <button onClick={opslaan} style={btn(C.pri, "#fff", C.pri)}>OK</button>
                <button onClick={() => setEdit(null)} style={btn(C.bg3, C.txt2, C.bdr)}>✕</button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontSize: 13, color: C.txt }}>{c.naam}</span>
                <button onClick={() => setEdit({ ...c })} style={btn(C.bg3, C.txt2, C.bdr)}>Bewerk</button>
                <button onClick={() => { if (confirm(t('Categorie "{naam}" verwijderen? Items verhuizen naar "Geen categorie".', { naam: c.naam }))) onDelete(c.id); }} style={btn(C.redBg, C.red, C.redBd)}><Trash2 size={13} /></button>
              </>
            )}
          </div>
        ))}
        {gefilterd.length === 0 && <p style={{ fontSize: 12, color: C.txt3, fontStyle: "italic" }}>{t("Nog geen categorieën in deze afdeling.")}</p>}
        <div style={{ display: "flex", gap: 8, marginTop: 4, paddingTop: 12, borderTop: `1px solid ${C.bdr}` }}>
          <input style={{ ...inp, flex: 1 }} placeholder={t("Nieuwe categorie…")} value={nieuw} onChange={e => setNieuw(e.target.value)} onKeyDown={e => e.key === "Enter" && toevoegen()} />
          <button onClick={toevoegen} style={btn(C.pri, "#fff", C.pri)}>+ {t("Toevoegen")}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GEDEELDE HELPERS
// ─────────────────────────────────────────────────────────────────────────────

// Popup wrapper
// ─────────────────────────────────────────────────────────────────────────────
// LOS ETIKET POPUP — een etiket printen voor iets dat geen mep-item is
// Vraagt alleen naam + THT-dagen; het aantal kies je in de PrintModal zelf.
// Maakt een klein "los item" dat verder niets in de data opslaat.
// ─────────────────────────────────────────────────────────────────────────────
function LosEtiketPopup({ thtNorm = 3, onClose, onVerder }) {
  const { t } = useT();
  const [naam, setNaam] = useState("");
  const [thtDagen, setThtDagen] = useState(thtNorm);
  const [fout, setFout] = useState(false);
  const naamRef = useRef();

  useEffect(() => { naamRef.current?.focus(); }, []);

  const verder = () => {
    if (!naam.trim()) { setFout(true); naamRef.current?.focus(); return; }
    // Los item: alleen wat de PrintModal nodig heeft. Geen id/opslag — dit
    // belandt nergens in de mep-data, het is puur een etiket.
    onVerder({
      naam: naam.trim(),
      thtDagen: Number(thtDagen) || thtNorm,
      allergenen: [],
      bewaaradvies: "",
      recept_id: null,
      los: true,
    });
  };

  return (
    <Popup onClose={onClose} title={t("Los etiket printen")} breedte={420}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ fontSize: 12.5, color: C.txt2, lineHeight: 1.5, background: C.bg3, border: `1px solid ${C.bdr}`, borderRadius: 8, padding: "9px 12px" }}>
          {t("Print snel een etiket voor iets dat niet in je lijst staat — een restje, een bereiding of een los product. Dit voegt niets toe aan je mep-lijst.")}
        </div>

        <Veld label={t("Naam *")}>
          <input
            ref={naamRef}
            style={{ ...inp, ...(fout ? { borderColor: C.red } : {}) }}
            placeholder={t("Wat komt er op het etiket?")}
            value={naam}
            onChange={e => { setNaam(e.target.value); setFout(false); }}
            onKeyDown={e => e.key === "Enter" && verder()}
          />
        </Veld>

        <Veld label={t("Bewaartermijn (dagen)")}>
          <input
            type="number" style={inp} min={0} max={365}
            placeholder={t("bijv. {n}", { n: thtNorm })}
            value={thtDagen ?? ""}
            onChange={e => setThtDagen(e.target.value)}
          />
          <div style={{ marginTop: 6, fontSize: 11.5, color: C.txt3, lineHeight: 1.5 }}>
            {t("Het aantal etiketten kies je in het volgende scherm.")}
          </div>
        </Veld>

        <div style={{ display: "flex", gap: 8, marginTop: 2, paddingTop: 14, borderTop: `1px solid ${C.bdr}` }}>
          <button onClick={verder} style={{ ...btn(C.pri, "#fff", C.pri), flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <Printer size={14} /> {t("Naar printen")}
          </button>
          <button onClick={onClose} style={btn(C.bg3, C.txt2, C.bdr)}>{t("Annuleren")}</button>
        </div>
      </div>
    </Popup>
  );
}

function Popup({ children, onClose, title, breedte = 480 }) {
  // Sluit op Escape
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.bg2, borderRadius: 12, width: "100%", maxWidth: breedte, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.25)" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", padding: "16px 20px", borderBottom: `1px solid ${C.bdr}` }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: C.txt, flex: 1 }}>{title}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, display: "flex", padding: 4 }}><X size={18} /></button>
        </div>
        {/* Body */}
        <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function Veld({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</label>
      {children}
    </div>
  );
}

// =============================================================================
// Zoekt het gekoppelde recept (uit de gedeelde recepten-cache) bij een mep-item,
// zodat de etiketprint ook klopt als 'ie los van het bewerk-scherm wordt geopend.
function resolveReceptVoorItem(item) {
  if (!item?.recept_id) return null;
  try {
    const s = localStorage.getItem("kc-recepten-v1");
    const recs = s ? (JSON.parse(s)?.recepten || []) : [];
    return recs.find(r => r.id === item.recept_id) || null;
  } catch { return null; }
}

// PRINT MODAL — Etiket printen via print.bijhenc.nl
// =============================================================================
const PRINT_SERVER_URL = "https://print.bijhenc.nl";
const GLUTEN_SUBS = ["Tarwe","Rogge","Gerst","Haver"];
const NOTEN_SUBS  = ["Amandelen","Hazelnoten","Walnoten","Cashewnoten","Pecannoten","Paranoten","Pistachenoten","Macadamianoten"];

async function sendPrintJob(payload, baseUrl) {
  const url = baseUrl || PRINT_SERVER_URL;
  try {
    const resp = await fetch(`${url}/print`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return await resp.json();
  } catch (err) {
    if (err.message?.includes("Failed to fetch") || err.message?.includes("NetworkError")) {
      return { ok: false, error: "Print server niet bereikbaar" };
    }
    return { ok: false, error: err.message };
  }
}

async function checkPrinter(baseUrl) {
  const url = baseUrl || PRINT_SERVER_URL;
  try {
    // Timeout ruim genoeg voor tragere wifi/tunnel: 3s was te strak, waardoor
    // tablets "offline" toonden terwijl de server prima draaide (het antwoord
    // kwam net te laat binnen). De server antwoordt met { ok: true, ... } — geen
    // printerOnline-veld — dus daar checken we op. Elke geslaagde reactie met
    // ok !== false telt als online.
    const r = await fetch(`${url}/status`, { signal: AbortSignal.timeout(8000) });
    if (!r.ok) return false;
    const d = await r.json();
    return d.ok !== false && d.printerOnline !== false;
  } catch { return false; }
}

function PrintModal({ item, recept, onClose, medewerkers = [], bedrijfsnaam = "", autoSluitNaPrint = false, onKlaar, printUrl }) {
  const { t } = useT();
  const vandaag = new Date().toISOString().slice(0, 10);
  const [prodDatum, setProdDatum] = useState(vandaag);
  const [thtDagen, setThtDagen] = useState(recept?.thtDagen || item?.thtDagen || 3);
  const [bewaarstatus, setBewaarstatus] = useState("koeling"); // KC_UI_V2_PRINTFLOW_PREVIEW
  const [toonDatums, setToonDatums] = useState(false);
  const [aantal, setAantal] = useState(1);
  const [medewerker, setMedewerker] = useState("");
  const [printing, setPrinting] = useState(false);
  const [status, setStatus] = useState(null); // null | 'ok' | 'error'
  const [statusMsg, setStatusMsg] = useState("");
  const [printerOnline, setPrinterOnline] = useState(null);

  // Medewerkers komen van MepModule (rechtstreeks uit team_data). Terugval op de
  // localStorage-cache als de lijst nog niet geladen is.
  const teamMeds = medewerkers?.length ? medewerkers : (() => {
    try { return JSON.parse(localStorage.getItem("kc-team-v1") || "{}")?.medewerkers || []; } catch { return []; }
  })();

  // Allergenen + bewaaradvies: van het recept als er één gekoppeld is, anders van het item zelf.
  // Op het etiket tonen we alleen de hoofd-allergenen (geen sub-categorieën).
  const allergenenBron = recept ? (recept.allergenen || []) : (item.allergenen || []);
  const allergenen = allergenenBron.filter(a =>
    !GLUTEN_SUBS.includes(a) && !NOTEN_SUBS.includes(a)
  );
  const basisBewaaradvies = recept ? (recept.bewaaradvies || "") : (item.bewaaradvies || "");
  // KC_UI_V2_PRINTFLOW_BEWAARSTATUS_LABEL
  // Het opgeslagen bewaaradvies blijft onaangetast. Alleen voor dit etiket wordt
  // het advies aangepast aan de gekozen bewaarstatus.
  const bewaaradvies = bewaarstatus === "vriezer"
    ? "Bewaren bij -18°C"
    : bewaarstatus === "uit_vriezer"
      ? "Na ontdooien gekoeld bewaren"
      : basisBewaaradvies;

  // THT berekening per bewaarstatus. In deze preview blijft de printpayload
  // achterwaarts compatibel: alleen de berekende THT-datum verandert.
  const berekenTHT = () => {
    if (!prodDatum) return "";
    const d = new Date(prodDatum);
    if (bewaarstatus === "vriezer") d.setMonth(d.getMonth() + 6);
    else if (bewaarstatus === "uit_vriezer") d.setDate(d.getDate() + 3);
    else d.setDate(d.getDate() + (parseInt(thtDagen) || 0));
    return d.toLocaleDateString("nl-NL");
  };
  const bewaarLabel = bewaarstatus === "vriezer" ? "Vriezer" : bewaarstatus === "uit_vriezer" ? "Uit vriezer" : "Koeling";

  const prodDatumNL = prodDatum ? new Date(prodDatum).toLocaleDateString("nl-NL") : "";
  const prodTijd = prodDatum === vandaag
    ? new Date().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })
    : "";
  const thtDatumNL = berekenTHT();

  // Check printer status bij openen
  useEffect(() => {
    checkPrinter(printUrl).then(online => setPrinterOnline(online));
  }, []);

  const print = async () => {
    setPrinting(true);
    setStatus(null);
    const n = Math.min(Math.max(parseInt(aantal) || 1, 1), 50);
    const result = await sendPrintJob({
      bedrijfsnaam: bedrijfsnaam || "",
      product:      item.naam,
      categorie:    "",
      datum:        prodDatumNL,
      datum_label:  bewaarstatus === "vriezer" ? "INGEVROREN OP" : bewaarstatus === "uit_vriezer" ? "ONTDOOID OP" : "PROD",
      tijd:         prodTijd,
      tht:          thtDatumNL,
      medewerker:   medewerker,
      allergenen:   allergenen,
      bewaaradvies: bewaaradvies,
      aantal:       n,
    }, printUrl);
    setPrinting(false);
    if (result.ok) {
      setStatus("ok");
      setStatusMsg(`✓ ${n} ${t(n === 1 ? "etiket" : "etiketten")} ${t("geprint")}`);
      // Medewerker-flow: na een geslaagde print kort de bevestiging tonen en dan
      // vanzelf terug naar de MEP-lijst. Beheerder-flow (autoSluitNaPrint=false)
      // blijft open en gebruikt de "Klaar"-knop hieronder.
      if (autoSluitNaPrint) {
        setTimeout(() => { onClose?.(); }, 1200);
      }
    } else {
      setStatus("error");
      setStatusMsg(`✗ ${result.error}`);
    }
  };

  // Sluit op Escape
  useEffect(() => {
    const h = e => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const inp2 = {
    background: C.bg2, border: `1px solid ${C.bdr}`, color: C.txt,
    padding: "6px 10px", fontFamily: "inherit", fontSize: 12,
    borderRadius: 6, outline: "none", width: "100%", boxSizing: "border-box",
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.bg2, borderRadius: 12, width: "100%", maxWidth: 520, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 80px rgba(0,0,0,.3)" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", padding: "14px 18px", borderBottom: `1px solid ${C.bdr}` }}>
          <Printer size={16} style={{ marginRight: 8, color: C.pri }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: C.txt, flex: 1 }}>{t("Etiket printen")}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: printerOnline === null ? C.txt3 : printerOnline ? C.green : C.red, marginRight: 10 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: printerOnline === null ? C.txt3 : printerOnline ? C.green : C.red }} />
            {printerOnline === null ? t("controleren…") : printerOnline ? t("printer online") : t("printer offline")}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, display: "flex" }}><X size={16} /></button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Live label preview */}
          <div style={{ background: "#fff", border: `2px solid ${C.hdr}`, borderRadius: 8, overflow: "hidden", fontFamily: "Arial, sans-serif" }}>
            {/* Banner */}
            <div style={{ background: C.hdr, padding: "6px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "#fff", fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>{bedrijfsnaam || "—"}</span>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 11, opacity: 0.8 }}>HACCP</span>
            </div>
            {/* Productnaam */}
            <div style={{ padding: "10px 12px 6px", fontSize: item.naam.length > 15 ? 18 : 22, fontWeight: 900, color: C.hdr, lineHeight: 1.1 }}>
              {item.naam}
            </div>
            {/* Scheidingslijn */}
            <div style={{ height: 2, background: C.hdr, margin: "0 12px" }} />
            {/* Datum / THT / medewerker */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", padding: "8px 12px", gap: 4 }}>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, color: C.txt3, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {bewaarstatus === "vriezer" ? "INGEVROREN" : bewaarstatus === "uit_vriezer" ? "ONTDOOID" : "PROD"}
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.hdr }}>{prodDatumNL || "—"}</div>
                {prodTijd && <div style={{ fontSize: 12, color: C.txt2 }}>{prodTijd}</div>}
                {medewerker && <div style={{ fontSize: 11, color: C.txt2, marginTop: 2 }}>DOOR: {medewerker}</div>}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: C.txt3, textTransform: "uppercase", letterSpacing: 0.5 }}>THT</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: C.hdr }}>{thtDatumNL || "—"}</div>
                <div style={{ fontSize: 9.5, fontWeight: 800, color: bewaarstatus === "koeling" ? C.txt3 : C.pri, marginTop: 2, textTransform: "uppercase", letterSpacing: .5 }}>{bewaarLabel}</div>
              </div>
            </div>
            {/* Allergenen */}
            {allergenen.length > 0 && (
              <div style={{ background: C.redBg, padding: "5px 12px", fontSize: 11, color: C.red, fontWeight: 700, borderTop: `1px solid ${C.redBd}` }}>
                ⚠ {allergenen.join(" · ")}
              </div>
            )}
            {/* Bewaaradvies */}
            {bewaaradvies && (
              <div style={{ padding: "4px 12px", fontSize: 10, color: C.txt2, borderTop: `1px solid ${C.bdr}` }}>
                🌡 {bewaaradvies}
              </div>
            )}
          </div>

          {/* Dagelijkse printkeuzes */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.txt2, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 }}>{t("Medewerker")}</label>
            <select style={{ ...inp2, height: 42 }} value={medewerker} onChange={e => setMedewerker(e.target.value)}>
              <option value="">{t("— kies medewerker —")}</option>
              {teamMeds.map(m => <option key={m.id} value={m.naam}>{m.naam}</option>)}
            </select>
          </div>

          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.txt2, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>{t("Bewaarstatus")}</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
              {[
                ["koeling", "Koeling", "normale THT"],
                ["vriezer", "Vriezer", "6 maanden"],
                ["uit_vriezer", "Uit vriezer", "3 dagen"],
              ].map(([id, label, sub]) => {
                const actief = bewaarstatus === id;
                return (
                  <button key={id} type="button" onClick={() => setBewaarstatus(id)}
                    style={{ border: `1.5px solid ${actief ? C.pri : C.bdr}`, background: actief ? C.priBg : C.bg2, color: actief ? C.pri : C.txt2, borderRadius: 9, padding: "9px 6px", cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>{t(label)}</div>
                    <div style={{ fontSize: 10, color: actief ? C.pri : C.txt3, marginTop: 2 }}>{t(sub)}</div>
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 7, padding: "7px 10px", background: C.bg3, borderRadius: 7, fontSize: 11.5, color: C.txt2 }}>
              {t("THT")}: <strong>{thtDatumNL || "—"}</strong> · {bewaarstatus === "koeling" ? `${thtDagen} ${t("dagen")}` : bewaarstatus === "vriezer" ? t("6 maanden") : t("3 dagen na ontdooien")}
            </div>
          </div>

          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.txt2, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 }}>{t("Aantal etiketten")}</label>
            <div style={{ display: "flex", alignItems: "center", border: `1px solid ${C.bdr}`, borderRadius: 9, overflow: "hidden", height: 44, maxWidth: 190 }}>
              <button type="button" aria-label={t("minder")} onClick={() => setAantal(a => Math.max(1, (parseInt(a) || 1) - 1))}
                style={{ width: 48, height: 44, border: "none", background: C.bg3, color: C.hdr, fontSize: 24, fontWeight: 700, lineHeight: 1, cursor: "pointer" }}>−</button>
              <div style={{ flex: 1, textAlign: "center", fontSize: 17, fontWeight: 800, color: C.hdr }}>{parseInt(aantal) || 1}</div>
              <button type="button" aria-label={t("meer")} onClick={() => setAantal(a => Math.min(50, (parseInt(a) || 1) + 1))}
                style={{ width: 48, height: 44, border: "none", background: C.bg3, color: C.hdr, fontSize: 24, fontWeight: 700, lineHeight: 1, cursor: "pointer" }}>+</button>
            </div>
          </div>

          <button type="button" onClick={() => setToonDatums(v => !v)}
            style={{ alignSelf: "flex-start", background: "none", border: "none", color: C.pri, padding: 0, fontFamily: "inherit", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
            {toonDatums ? t("Datum/THT verbergen") : t("Datum/THT aanpassen")}
          </button>

          {toonDatums && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: 10, background: C.bg3, borderRadius: 8 }}>
              <div>
                <label style={{ fontSize: 10.5, fontWeight: 700, color: C.txt2, display: "block", marginBottom: 4 }}>{t("Datum")}</label>
                <input type="date" style={inp2} value={prodDatum} max={vandaag} onChange={e => setProdDatum(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 10.5, fontWeight: 700, color: C.txt2, display: "block", marginBottom: 4 }}>{t("Koeling THT (dagen)")}</label>
                <input type="number" style={inp2} value={thtDagen} min={0} max={365} disabled={bewaarstatus !== "koeling"}
                  onChange={e => setThtDagen(e.target.value)} />
              </div>
            </div>
          )}

          {!recept && (
            <div style={{ fontSize: 11, color: C.txt3, fontStyle: "italic" }}>
              {t('Geen recept gekoppeld — allergenen en bewaaradvies komen van het item zelf (aan te passen via "Item bewerken").')}
            </div>
          )}

          {/* Status */}
          {status && (
            <div style={{ padding: "8px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: status === "ok" ? C.greenBg : C.redBg, color: status === "ok" ? C.green : C.red, border: `1px solid ${status === "ok" ? C.greenBd : C.redBd}` }}>
              {statusMsg}
            </div>
          )}

          {/* Print knop */}
          <button onClick={print} disabled={printing || printerOnline === false}
            style={{ background: printing || printerOnline === false ? C.bdr : C.hdr, border: "none", color: printing || printerOnline === false ? C.txt3 : "#fff", padding: "11px 14px", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: printing || printerOnline === false ? "not-allowed" : "pointer", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Printer size={16} />
            {printing ? t("Printen…") : printerOnline === false ? t("Printer offline") : (parseInt(aantal) || 1) > 1 ? t("Print {n} etiketten", { n: parseInt(aantal) || 1 }) : t("Print etiket")}
          </button>

          {/* Beheerder-flow: na een geslaagde print zelf terug naar de MEP-lijst.
              (Medewerker-flow sluit automatisch en ziet deze knop dus niet.) */}
          {!autoSluitNaPrint && status === "ok" && onKlaar && (
            <button onClick={onKlaar}
              style={{ background: C.greenBg, border: `1px solid ${C.greenBd}`, color: C.green, padding: "11px 14px", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <Check size={16} /> {t("Klaar — terug naar lijst")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

